import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Nav from "../components/Nav";
import { api, formatApiError } from "../lib/api";
import { compressImage } from "../lib/imageCompress";
import { useAuth } from "../context/AuthContext";
import AccountSecurity from "../components/AccountSecurity";
import { toast } from "sonner";
import { ALL_PROVINCES, AREAS_BY_PROVINCE, labelForArea, provinceForArea } from "../lib/areas";
import { Camera, PencilSimple, Flag, X, CheckCircle, ChatCircleText, SignOut, FacebookLogo, InstagramLogo, YoutubeLogo, LinkedinLogo, WhatsappLogo, Globe, Printer, FilePdf, Eye, Bell, ClipboardText, Wrench, ShieldCheck, ClockCounterClockwise, Plus } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Supported media platforms — order = display order on the public profile.
// "Documents" is a link to the cardholder's OWN storage (Google Drive / Dropbox /
// OneDrive / Notion etc.) — Curb Crew never stores the documents themselves.
const LINK_PRESETS = [
  { key: "Website",   icon: Globe,         placeholder: "www.yoursite.co.za" },
  { key: "Facebook",  icon: FacebookLogo,  placeholder: "www.facebook.com/your.page" },
  { key: "Instagram", icon: InstagramLogo, placeholder: "www.instagram.com/your.handle" },
  { key: "YouTube",   icon: YoutubeLogo,   placeholder: "www.youtube.com/@your.channel" },
  { key: "LinkedIn",  icon: LinkedinLogo,  placeholder: "www.linkedin.com/in/your.handle" },
  { key: "WhatsApp",  icon: WhatsappLogo,  placeholder: "wa.me/27821234567" },
  { key: "Documents", icon: FilePdf,       placeholder: "drive.google.com/drive/folders/..." },
];

const linkIconFor = (label) => {
  const p = LINK_PRESETS.find((x) => x.key.toLowerCase() === (label || "").toLowerCase());
  return p ? p.icon : Globe;
};

// Card-type visual + capability map. Mirrors /cards comparison page so the
// dashboard tile colour, the modal accent strip, and the conditional render
// rules all stay in one place. Add a new card type? Update this map first.
const CARD_TYPE_VISUALS = {
  curb_crew:      { label: "Curb Crew",      stripBg: "bg-[color:var(--cc-yellow)]", stripText: "text-black", chipBg: "bg-[color:var(--cc-yellow)] text-black border-black" },
  curb_pro:       { label: "Curb Pro",       stripBg: "bg-black",                    stripText: "text-white", chipBg: "bg-black text-white border-black" },
  curb_assets:    { label: "Curb Retailer", stripBg: "bg-[#C2410C]",                stripText: "text-white", chipBg: "bg-[#C2410C] text-white border-black" },
  curb_medic:     { label: "Curb Medic",     stripBg: "bg-[#DC2626]",                stripText: "text-white", chipBg: "bg-[#DC2626] text-white border-black" },
  curb_traveller: { label: "Curb Traveller", stripBg: "bg-[#0EA5E9]",                stripText: "text-white", chipBg: "bg-[#0EA5E9] text-white border-black" },
  curb_auto:      { label: "Curb Auto",      stripBg: "bg-[#16A34A]",                stripText: "text-white", chipBg: "bg-[#16A34A] text-white border-black" },
};
const visualFor = (type) => CARD_TYPE_VISUALS[type] || CARD_TYPE_VISUALS.curb_pro;



// Cascading Province -> Town picker. `value` is an array of area codes (any province).
// User picks a province, the town list filters, they click towns to add. Selected towns
// appear as removable chips above. Hard cap enforced via `max`.
function AreaPicker({ value, onChange, max, testId, label, hint }) {
  const [province, setProvince] = React.useState(() => provinceForArea(value?.[0]) || "");
  const towns = province ? AREAS_BY_PROVINCE[province] : [];

  const add = (code) => {
    if (value.includes(code) || value.length >= max) return;
    onChange([...value, code]);
  };
  const remove = (code) => onChange(value.filter((c) => c !== code));

  return (
    <div data-testid={testId}>
      <label className="label-tag">{label} <span className="text-neutral-500">({value.length}/{max})</span></label>

      {/* Selected chips */}
      {value.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`${testId}-chips`}>
          {value.map((code) => (
            <span key={code} className="inline-flex items-center gap-1 px-2 py-1 bg-[color:var(--cc-yellow)] border border-black text-[11px] font-bold">
              {labelForArea(code)}
              <button
                type="button"
                onClick={() => remove(code)}
                className="hover:text-red-700"
                aria-label={`Remove ${labelForArea(code)}`}
                data-testid={`${testId}-chip-remove-${code}`}
              >
                <X size={10} weight="bold" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Cascading selects */}
      <div className="mt-2 grid grid-cols-1 gap-2">
        <select
          value={province}
          onChange={(e) => setProvince(e.target.value)}
          className="w-full px-3 py-2 border border-black outline-none text-sm bg-white"
          data-testid={`${testId}-province`}
        >
          <option value="">Pick a province / country…</option>
          {ALL_PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>

        <select
          value=""
          disabled={!province || value.length >= max}
          onChange={(e) => { if (e.target.value) add(e.target.value); }}
          className="w-full px-3 py-2 border border-black outline-none text-sm bg-white disabled:bg-neutral-100 disabled:text-neutral-400"
          data-testid={`${testId}-town`}
        >
          <option value="">
            {!province ? "Pick a province first…" : value.length >= max ? `Max ${max} reached — remove one to add more` : "Add a town…"}
          </option>
          {towns
            .filter((t) => !value.includes(t.code))
            .map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
        </select>
      </div>

      {hint && <div className="label-tag text-neutral-500 mt-1">{hint}</div>}
    </div>
  );
}

// Notice Board panel — one per cardholder card. Lists active (non-expired)
// posts, lets the cardholder create up to 5, and delete any of them. Posts
// auto-expire on read (the backend filters by expires_at) so we don't need
// a client-side timer.
function NoticeBoard({ card }) {
  const [posts, setPosts] = React.useState([]);
  const [max, setMax] = React.useState(5);
  const [showForm, setShowForm] = React.useState(false);
  const [title, setTitle] = React.useState("");
  const [body, setBody] = React.useState("");
  const [days, setDays] = React.useState(7);
  const [busy, setBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/cardholder/cards/${card.id}/noticeboard`);
      setPosts(data.posts || []);
      setMax(data.max_active || 5);
    } catch (e) {
      // Silent — empty state is fine if the call fails
    } finally {
      setLoaded(true);
    }
  }, [card.id]);

  React.useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    setBusy(true);
    try {
      await api.post(`/cardholder/cards/${card.id}/noticeboard`, {
        title: title.trim(),
        body: body.trim(),
        expires_days: days,
      });
      toast.success("Posted — visitors will see it on your card");
      setTitle(""); setBody(""); setDays(7); setShowForm(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const remove = async (postId) => {
    if (!window.confirm("Delete this post? It will disappear from your public card immediately.")) return;
    try {
      await api.delete(`/cardholder/cards/${card.id}/noticeboard/${postId}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Format the expiry as a short relative string ("expires in 3 days").
  const expiresIn = (iso) => {
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return "expired";
    const d = Math.floor(ms / (1000 * 60 * 60 * 24));
    const h = Math.floor((ms / (1000 * 60 * 60)) % 24);
    if (d > 0) return `expires in ${d} day${d === 1 ? "" : "s"}`;
    return `expires in ${h} hour${h === 1 ? "" : "s"}`;
  };

  const atCap = posts.length >= max;

  return (
    <div className="mt-3 border-t border-dashed border-neutral-400 pt-3" data-testid={`noticeboard-${card.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <ClipboardText size={16} weight="bold" />
          <div className="label-tag">Notice Board <span className="text-neutral-500">({posts.length}/{max} active)</span></div>
        </div>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            disabled={atCap}
            className={`label-tag px-3 py-1 border-2 border-black brutal-btn text-xs ${atCap ? "bg-neutral-200 text-neutral-500" : "bg-[color:var(--cc-yellow)]"}`}
            data-testid={`noticeboard-add-${card.id}`}
          >
            {atCap ? "Cap reached" : "+ Add post"}
          </button>
        )}
      </div>

      <div className="text-[11px] text-neutral-600 mt-1">
        Short updates shown on your public card. <strong>"Available this weekend"</strong> · <strong>"Fresh stock in"</strong> · <strong>"R200 off geyser installs"</strong>. Each post auto-expires.
      </div>

      {showForm && (
        <form onSubmit={submit} className="mt-3 border-2 border-black bg-white p-3 space-y-2" data-testid={`noticeboard-form-${card.id}`}>
          <div>
            <label className="label-tag">Title <span className="text-neutral-500">({title.length}/60)</span></label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 60))}
              placeholder="Available this weekend"
              className="mt-1 w-full px-3 py-2 border border-black outline-none text-sm"
              data-testid={`noticeboard-title-${card.id}`}
            />
          </div>
          <div>
            <label className="label-tag">Message <span className="text-neutral-500">({body.length}/240)</span></label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, 240))}
              placeholder="Booking emergency callouts Sat + Sun. Cape Town only."
              rows={3}
              className="mt-1 w-full px-3 py-2 border border-black outline-none text-sm resize-none"
              data-testid={`noticeboard-body-${card.id}`}
            />
          </div>
          <div>
            <label className="label-tag">Auto-expires after</label>
            <div className="mt-1 flex gap-2">
              {[7, 14, 30].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDays(d)}
                  className={`label-tag px-3 py-1 border-2 border-black brutal-btn text-xs ${days === d ? "bg-black text-white" : "bg-white"}`}
                  data-testid={`noticeboard-days-${d}-${card.id}`}
                >
                  {d} days
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={busy || !title.trim() || !body.trim()}
              className="label-tag px-4 py-2 bg-black text-white border-2 border-black brutal-btn text-xs disabled:opacity-50"
              data-testid={`noticeboard-submit-${card.id}`}
            >{busy ? "Posting…" : "Post"}</button>
            <button
              type="button"
              onClick={() => { setShowForm(false); setTitle(""); setBody(""); }}
              className="label-tag px-4 py-2 bg-white border-2 border-black brutal-btn text-xs"
            >Cancel</button>
          </div>
        </form>
      )}

      {loaded && posts.length === 0 && !showForm && (
        <div className="mt-3 text-xs text-neutral-500 italic">No posts yet — add one to show on your public card.</div>
      )}

      {posts.length > 0 && (
        <ul className="mt-3 space-y-2">
          {posts.map((p) => (
            <li key={p.id} className="border-2 border-black bg-[color:var(--cc-bg-2)] p-2" data-testid={`noticeboard-post-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-display font-black text-sm tracking-tight">{p.title}</div>
                  <div className="text-xs text-neutral-700 mt-0.5">{p.body}</div>
                  <div className="label-tag text-neutral-500 mt-1 text-[10px]">{expiresIn(p.expires_at)}</div>
                </div>
                <button
                  type="button"
                  onClick={() => remove(p.id)}
                  className="p-1 border border-black bg-white"
                  aria-label="Delete post"
                  data-testid={`noticeboard-delete-${p.id}`}
                ><X size={12} weight="bold" /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// AssetManager — per-card panel that lets the cardholder mint secondary
// QR stickers tied to specific physical items (geyser, vehicle, listing
// etc). Each asset gets a public /a/{code} page with a fault-report form
// that emails the cardholder. Capped to keep the dashboard sane.
function AssetManager({ card }) {
  const [assets, setAssets] = React.useState([]);
  const [max, setMax] = React.useState(25);
  const [loaded, setLoaded] = React.useState(false);
  const [showForm, setShowForm] = React.useState(false);
  const [showClaim, setShowClaim] = React.useState(false);
  const [claimCode, setClaimCode] = React.useState("");
  const [claimBusy, setClaimBusy] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  // editingId: null = create mode; asset.id = edit mode (form pre-filled).
  const [editingId, setEditingId] = React.useState(null);
  // Ref to the form element so openEdit() can scroll it into view + focus
  // the first input. Without this the form renders ABOVE the assets table
  // and a click on a row deep in the list opens the form off-screen at the
  // top — users perceive this as "edit button doesn't work".
  const formRef = React.useRef(null);
  const labelInputRef = React.useRef(null);
  // Tracks which asset_code is currently expanded to show its service
  // history + log-service mini-form. Only one row open at a time keeps
  // the table compact on mobile.
  const [historyOpenCode, setHistoryOpenCode] = React.useState(null);
  // Table state: search query + sort column + sort direction. Scales the
  // dashboard to hundreds of assets without overwhelming the cardholder.
  const [search, setSearch] = React.useState("");
  const [sortKey, setSortKey] = React.useState("created_at");
  const [sortDir, setSortDir] = React.useState("desc");
  const [form, setForm] = React.useState({ label: "", item: "", make: "", model: "", serial: "", notes: "", service_interval_months: 0, next_service_at: "", owner_card_code: "",
    // Curb Auto extras — only rendered when card.card_type === "curb_auto".
    vin: "", license_expiry: "",
    tires_size: "", tires_date: "", tires_km: "", tires_alignment_date: "", tires_punctures: "",
    battery_make: "", battery_date: "", battery_size: "",
    insurance_company: "", insurance_phone: "", broker_phone: "",
    tow_company: "", tow_phone: "",
    customer_name: "", customer_email: "", customer_phone: "",
  });

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/cardholder/cards/${card.id}/assets`);
      setAssets(data.assets || []);
      setMax(data.max || 25);
    } catch (e) { /* silent */ }
    finally { setLoaded(true); }
  }, [card.id]);

  React.useEffect(() => { load(); }, [load]);

  // Surface a one-shot success banner when the cardholder arrives here
  // straight after claiming an unbound sticker via /a/<CODE>. Asset.jsx
  // navigates to /my-cards?claimed=<CODE> on success; without this toast
  // the user lands on the dashboard with no clear "you just claimed XYZ,
  // go label it" cue — they hit "+ Manual entry" instead and generate a
  // new code, which is exactly the bug we're closing.
  const [params, setParams] = useSearchParams();
  const claimedCode = params.get("claimed") || "";
  const justClaimedAsset = React.useMemo(
    () => (claimedCode ? assets.find((a) => a.asset_code === claimedCode.toUpperCase()) : null),
    [claimedCode, assets],
  );
  React.useEffect(() => {
    if (claimedCode && loaded && justClaimedAsset) {
      toast.success(`Sticker ${claimedCode.toUpperCase()} linked to this card — tap the pencil icon below to label it.`, {
        duration: 8000,
      });
      // Clear the param so refresh / back-nav doesn't re-fire the toast.
      const next = new URLSearchParams(params);
      next.delete("claimed");
      setParams(next, { replace: true });
    }
  }, [claimedCode, loaded, justClaimedAsset, params, setParams]);

  const resetForm = () => {
    setForm({ label: "", item: "", make: "", model: "", serial: "", notes: "", service_interval_months: 0, next_service_at: "", owner_card_code: "",
      vin: "", license_expiry: "",
      tires_size: "", tires_date: "", tires_km: "", tires_alignment_date: "", tires_punctures: "",
      battery_make: "", battery_date: "", battery_size: "",
      insurance_company: "", insurance_phone: "", broker_phone: "",
      tow_company: "", tow_phone: "",
      customer_name: "", customer_email: "", customer_phone: "",
    });
    setEditingId(null);
    setShowForm(false);
  };

  const openEdit = (a) => {
    setForm({
      label: a.label || "",
      item: a.item || "",
      make: a.make || "",
      model: a.model || "",
      serial: a.serial || "",
      notes: a.notes || "",
      service_interval_months: a.service_interval_months || 0,
      next_service_at: a.next_service_at || "",
      owner_card_code: a.owner_card_code || "",
      vin: a.vin || "",
      license_expiry: a.license_expiry || "",
      tires_size: a.tires_size || "",
      tires_date: a.tires_date || "",
      tires_km: a.tires_km ?? "",
      tires_alignment_date: a.tires_alignment_date || "",
      tires_punctures: a.tires_punctures || "",
      battery_make: a.battery_make || "",
      battery_date: a.battery_date || "",
      battery_size: a.battery_size || "",
      insurance_company: a.insurance_company || "",
      insurance_phone: a.insurance_phone || "",
      broker_phone: a.broker_phone || "",
      tow_company: a.tow_company || "",
      tow_phone: a.tow_phone || "",
      customer_name: a.customer_name || "",
      customer_email: a.customer_email || "",
      customer_phone: a.customer_phone || "",
    });
    setEditingId(a.id);
    setShowForm(true);
    setShowClaim(false);
    // Wait one tick for the form to mount, then scroll it into view and
    // focus the Label input. This is the actual fix for the long-standing
    // "edit button doesn't work" complaint — the form was opening above
    // the table, off-screen, so users thought the click did nothing.
    setTimeout(() => {
      const node = formRef.current;
      if (node && typeof node.scrollIntoView === "function") {
        node.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      const input = labelInputRef.current;
      if (input && typeof input.focus === "function") {
        // select() so the user can immediately overwrite the placeholder
        // label without first having to clear it.
        input.focus();
        try { input.select(); } catch { /* not all input types support select() */ }
      }
    }, 30);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) return;
    setBusy(true);
    try {
      if (editingId) {
        // Edit mode — PATCH only the asset fields, then sync homeowner separately.
        const { owner_card_code, ...assetFields } = form;
        await api.patch(`/cardholder/cards/${card.id}/assets/${editingId}`, assetFields);
        // Sync homeowner link if changed.
        const current = assets.find((a) => a.id === editingId);
        const before = (current && current.owner_card_code) || "";
        const after = (owner_card_code || "").trim().toUpperCase();
        if (before !== after) {
          try {
            await api.patch(`/cardholder/cards/${card.id}/assets/${editingId}/owner`, { owner_card_code: after });
          } catch (e) {
            toast.warning(`Saved, but homeowner link failed: ${formatApiError(e)}`);
          }
        }
        toast.success("Asset updated");
      } else {
        const { data } = await api.post(`/cardholder/cards/${card.id}/assets`, { cardholder_id: card.id, ...form });
        // If the artisan filled in a homeowner card code, link it now.
        if (form.owner_card_code && form.owner_card_code.trim()) {
          try {
            await api.patch(`/cardholder/cards/${card.id}/assets/${data.asset.id}/owner`, { owner_card_code: form.owner_card_code.trim() });
          } catch (e) {
            toast.warning(`Asset created, but couldn't link homeowner: ${formatApiError(e)}`);
          }
        }
        toast.success("Asset created");
      }
      resetForm();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const claim = async (e) => {
    e.preventDefault();
    if (!claimCode.trim()) return;
    setClaimBusy(true);
    try {
      await api.post(`/cardholder/cards/${card.id}/assets/claim`, { asset_code: claimCode.trim() });
      toast.success(`Sticker claimed — open it from the list below to label it`);
      setClaimCode("");
      setShowClaim(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setClaimBusy(false); }
  };

  const remove = async (assetId) => {
    if (!window.confirm("Delete this asset? The QR sticker will stop working. Any fault reports for this asset are also deleted.")) return;
    try {
      await api.delete(`/cardholder/cards/${card.id}/assets/${assetId}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const atCap = assets.length >= max;

  // Search + sort the asset list before render. Memoised so 300+ assets
  // don't refilter on every keystroke unless inputs actually change.
  const filteredSortedAssets = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = !q ? assets.slice() : assets.filter((a) => {
      const hay = `${a.label || ""} ${a.item || ""} ${a.make || ""} ${a.model || ""} ${a.serial || ""} ${a.asset_code || ""} ${a.notes || ""}`.toLowerCase();
      return hay.includes(q);
    });
    const dir = sortDir === "asc" ? 1 : -1;
    filtered.sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return filtered;
  }, [assets, search, sortKey, sortDir]);

  // Per-card-type label so "My Assets" doesn't feel weird on a Curb Auto
  // shop or a Curb Assets estate-agent. Same data table, three different
  // real-world meanings (the user spotted this — Feb 2026).
  const FACE_LABELS = {
    curb_pro:    { title: "My Installations", hint: "Geysers, aircons, gates I installed — anyone scanning the sticker reports faults back to me." },
    curb_assets: { title: "My Shop & Stock", hint: "Products, racks, fixtures or POS gear I sell or track — each gets its own scannable sticker." },
    curb_auto:   { title: "Customer Vehicles", hint: "Vehicles where I fitted tires / batteries — log work, surface due-soon customers, push specials." },
  };
  const faceLabel = FACE_LABELS[card.card_type] || FACE_LABELS.curb_pro;

  return (
    <div className="mt-3 border-t border-dashed border-neutral-400 pt-3" data-testid={`assetmanager-${card.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <FilePdf size={16} weight="bold" />
          <div className="label-tag">{faceLabel.title} <span className="text-neutral-500">({assets.length}/{max})</span></div>
        </div>
        {!showForm && !showClaim && (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setShowClaim(true)}
              disabled={atCap}
              className={`label-tag px-3 py-1 border-2 border-black brutal-btn text-xs ${atCap ? "bg-neutral-200 text-neutral-500" : "bg-[color:var(--cc-yellow)]"}`}
              data-testid={`assetmanager-claim-${card.id}`}
              title="Got a printed sticker? Type the code from the back to link it to this card."
            >
              {atCap ? "Cap reached" : "Claim sticker"}
            </button>
            {/* Manual entry button was removed (Feb 2026 — cardholder request).
                Every legitimate Curb Crew asset starts as a printed sticker
                bound via Claim sticker — that's the whole point of the
                product. Manual entry generated a fresh asset_code that
                didn't match any physical sticker, which kept being mistaken
                for a claim flow and creating duplicate "orphan" codes.
                The create-asset endpoint (POST /cardholder/cards/<id>/assets)
                still works and is reachable by the Edit flow + future bulk
                import tools, just not by a top-level dashboard button. */}
          </div>
        )}
      </div>
      <div className="text-[11px] text-neutral-600 mt-1">
        {faceLabel.hint} <strong>Got a printed sticker?</strong> Tap <span className="bg-[color:var(--cc-yellow)] px-1.5 py-0.5 border border-black">Claim sticker</span> and type the code from the back of the sticker.
      </div>

      {showClaim && (
        <form onSubmit={claim} className="mt-3 border-2 border-black bg-[color:var(--cc-yellow)] p-3 space-y-2" data-testid={`assetmanager-claim-form-${card.id}`}>
          <label className="label-tag">Sticker code from your retail pack</label>
          <input
            value={claimCode}
            onChange={(e) => setClaimCode(e.target.value.toUpperCase().slice(0, 12))}
            placeholder="A8BJL4C"
            autoFocus
            className="mt-1 w-full px-3 py-2 border-2 border-black outline-none text-sm font-mono-cc tracking-wider"
            data-testid={`assetmanager-claim-input-${card.id}`}
          />
          <div className="text-[11px] text-neutral-700">7 characters starting with <strong>A</strong>. Find it on the back of any sticker in the pack.</div>
          <div className="flex gap-2 pt-1">
            <button type="submit" disabled={claimBusy || !claimCode.trim()} className="label-tag px-4 py-2 bg-black text-white border-2 border-black brutal-btn text-xs disabled:opacity-50" data-testid={`assetmanager-claim-submit-${card.id}`}>{claimBusy ? "Claiming…" : "Claim"}</button>
            <button type="button" onClick={() => { setShowClaim(false); setClaimCode(""); }} className="label-tag px-4 py-2 bg-white border-2 border-black brutal-btn text-xs">Cancel</button>
          </div>
        </form>
      )}

      {showForm && (
        <form ref={formRef} onSubmit={submit} className="mt-3 border-2 border-black bg-white p-3 space-y-2" data-testid={`assetmanager-form-${card.id}`}>
          <div className="label-tag text-neutral-500 mb-1">{editingId ? "Edit asset" : "New asset"}</div>          <div>
            <label className="label-tag">{card.card_type === "curb_auto" ? "Vehicle ID" : "Label"} <span className="text-neutral-500">(what / where — visible to scanner)</span></label>
            <input
              ref={labelInputRef}
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value.slice(0, 80) })}
              placeholder={card.card_type === "curb_auto" ? "Hilux JJW 123 GP" : "Geyser - 12 Oak Street"}
              className="mt-1 w-full px-3 py-2 border border-black outline-none text-sm"
              data-testid={`asset-label-input-${card.id}`}
            />
          </div>
          {card.card_type === "curb_auto" ? (
            // For a workshop the generic Make / Model / Serial fields make
            // no sense for a car (a Hilux doesn't have a "serial number"
            // visible to the workshop). Lead with a single "Vehicle"
            // text field that captures make + model + trim in one line —
            // the detailed vehicle data lives in the Curb Auto block
            // below (VIN, license expiry, tires, battery, insurance, etc.).
            <div>
              <label className="label-tag">Vehicle <span className="text-neutral-500">(make · model · trim)</span></label>
              <input
                value={form.item}
                onChange={(e) => setForm({ ...form, item: e.target.value.slice(0, 80) })}
                placeholder="Toyota Hilux 2.4 GD-6"
                className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm"
                data-testid={`asset-vehicle-input-${card.id}`}
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label-tag">Item</label>
                <input value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} placeholder="Geyser" className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm" />
              </div>
              <div>
                <label className="label-tag">Make</label>
                <input value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} placeholder="Kwikot" className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm" />
              </div>
              <div>
                <label className="label-tag">Model</label>
                <input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="150L Electric" className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm" />
              </div>
              <div>
                <label className="label-tag">Serial</label>
                <input value={form.serial} onChange={(e) => setForm({ ...form, serial: e.target.value })} placeholder="KW-2024…" className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm font-mono-cc" />
              </div>
            </div>
          )}
          <div>
            <label className="label-tag">{card.card_type === "curb_auto" ? "Workshop notes" : "Notes"} <span className="text-neutral-500">(private)</span></label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value.slice(0, 240) })} placeholder={card.card_type === "curb_auto" ? "Fitted new front brake pads. Customer waiting on cambelt quote." : "Installed 2024-08. Pressure valve checked."} rows={2} className="mt-1 w-full px-3 py-2 border border-black outline-none text-sm resize-none" />
          </div>

          {/* Curb Auto fields — only when the merchant's card type is
              curb_auto. Designed for a tire/battery/auto shop logging
              what they fitted onto a customer's vehicle. Public scan
              only sees the emergency-safe subset (see backend
              public_asset filter). Everything below is workshop-internal. */}
          {card.card_type === "curb_auto" && (
            <div className="border-2 border-[#16A34A] bg-[#F0FDF4] p-3 space-y-2" data-testid={`asset-auto-fields-${card.id}`}>
              <div className="label-tag bg-[#16A34A] text-white px-2 py-1 inline-block">CURB AUTO · VEHICLE DETAILS</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="label-tag">VIN <span className="text-neutral-500">(private)</span></label>
                  <input value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase().slice(0, 32) })} placeholder="WBA12345…" className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm font-mono-cc" data-testid={`auto-vin-${card.id}`} />
                </div>
                <div>
                  <label className="label-tag">License renewal date</label>
                  <input type="date" value={form.license_expiry.slice(0,10)} onChange={(e) => setForm({ ...form, license_expiry: e.target.value })} className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm" data-testid={`auto-license-${card.id}`} />
                </div>
                <div>
                  <label className="label-tag">Tire size</label>
                  <input value={form.tires_size} onChange={(e) => setForm({ ...form, tires_size: e.target.value.slice(0, 32) })} placeholder="205/55R16" className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm font-mono-cc" data-testid={`auto-tires-size-${card.id}`} />
                </div>
                <div>
                  <label className="label-tag">Tires fitted (date)</label>
                  <input type="date" value={form.tires_date.slice(0,10)} onChange={(e) => setForm({ ...form, tires_date: e.target.value })} className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm" data-testid={`auto-tires-date-${card.id}`} />
                </div>
                <div>
                  <label className="label-tag">Tires fitted (km)</label>
                  <input type="number" value={form.tires_km} onChange={(e) => setForm({ ...form, tires_km: e.target.value.replace(/[^\d]/g, "") })} placeholder="124500" className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm font-mono-cc" data-testid={`auto-tires-km-${card.id}`} />
                </div>
                <div>
                  <label className="label-tag">Alignment date</label>
                  <input type="date" value={form.tires_alignment_date.slice(0,10)} onChange={(e) => setForm({ ...form, tires_alignment_date: e.target.value })} className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm" />
                </div>
                <div>
                  <label className="label-tag">Battery make</label>
                  <input value={form.battery_make} onChange={(e) => setForm({ ...form, battery_make: e.target.value.slice(0, 60) })} placeholder="Willard / Raylite" className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm" data-testid={`auto-bat-make-${card.id}`} />
                </div>
                <div>
                  <label className="label-tag">Battery fitted (date)</label>
                  <input type="date" value={form.battery_date.slice(0,10)} onChange={(e) => setForm({ ...form, battery_date: e.target.value })} className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm" data-testid={`auto-bat-date-${card.id}`} />
                </div>
                <div>
                  <label className="label-tag">Battery size</label>
                  <input value={form.battery_size} onChange={(e) => setForm({ ...form, battery_size: e.target.value.slice(0, 40) })} placeholder="630 / 644 …" className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm font-mono-cc" />
                </div>
                <div>
                  <label className="label-tag">Punctures</label>
                  <input value={form.tires_punctures} onChange={(e) => setForm({ ...form, tires_punctures: e.target.value.slice(0, 120) })} placeholder="2 (rear left, Feb)" className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm" />
                </div>
              </div>

              <div className="label-tag text-neutral-700 pt-1">Emergency / insurance <span className="text-neutral-500 normal-case">(public-safe)</span></div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="label-tag">Insurance company</label>
                  <input value={form.insurance_company} onChange={(e) => setForm({ ...form, insurance_company: e.target.value.slice(0, 80) })} placeholder="Outsurance / Discovery" className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm" data-testid={`auto-ins-co-${card.id}`} />
                </div>
                <div>
                  <label className="label-tag">Claim line</label>
                  <input value={form.insurance_phone} onChange={(e) => setForm({ ...form, insurance_phone: e.target.value.slice(0, 32) })} placeholder="0860..." className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm font-mono-cc" name="cc-auto-insurance-phone" autoComplete="off" />
                </div>
                <div>
                  <label className="label-tag">Broker phone <span className="text-neutral-500">(private)</span></label>
                  <input value={form.broker_phone} onChange={(e) => setForm({ ...form, broker_phone: e.target.value.slice(0, 32) })} placeholder="0820000000" className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm font-mono-cc" name="cc-auto-broker-phone" autoComplete="off" />
                </div>
                <div>
                  <label className="label-tag">Tow company</label>
                  <input value={form.tow_company} onChange={(e) => setForm({ ...form, tow_company: e.target.value.slice(0, 80) })} placeholder="Joe's Towing" className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm" name="cc-auto-tow-company" autoComplete="off" />
                </div>
                <div>
                  <label className="label-tag">Tow phone</label>
                  <input value={form.tow_phone} onChange={(e) => setForm({ ...form, tow_phone: e.target.value.slice(0, 32) })} placeholder="0820000000" className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm font-mono-cc" data-testid={`auto-tow-phone-${card.id}`} name="cc-auto-tow-phone" autoComplete="off" />
                </div>
              </div>

              <div className="label-tag text-neutral-700 pt-1">Customer <span className="text-neutral-500 normal-case">(for retention pushes — private)</span></div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="label-tag">Customer name</label>
                  <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value.slice(0, 80) })} placeholder="J Smith" className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm" data-testid={`auto-cust-name-${card.id}`} />
                </div>
                <div>
                  <label className="label-tag">Customer email</label>
                  <input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value.slice(0, 120) })} placeholder="js@example.com" className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm" data-testid={`auto-cust-email-${card.id}`} />
                </div>
                <div>
                  <label className="label-tag">Customer phone</label>
                  <input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value.slice(0, 32) })} placeholder="0820000000" className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm font-mono-cc" data-testid={`auto-cust-phone-${card.id}`} name="cc-auto-customer-phone" autoComplete="off" />
                </div>
              </div>
            </div>
          )}
          <div>
            <label className="label-tag">Homeowner's Curb Crew card code <span className="text-neutral-500">(optional)</span></label>
            <input
              value={form.owner_card_code || ""}
              onChange={(e) => setForm({ ...form, owner_card_code: e.target.value.toUpperCase().slice(0, 12) })}
              placeholder="e.g. 984ZFFSM"
              className="mt-1 w-full px-3 py-2 border border-black outline-none text-sm font-mono-cc tracking-wider"
              data-testid={`asset-owner-code-${card.id}`}
            />
            <div className="text-[10px] text-neutral-500 mt-1">If the homeowner has a Curb Crew card, link it here. They'll see this asset in their dashboard and can swap you out later if needed.</div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label-tag">Service every</label>
              <select
                value={form.service_interval_months}
                onChange={(e) => setForm({ ...form, service_interval_months: Number(e.target.value) })}
                className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm bg-white"
                data-testid={`asset-interval-${card.id}`}
              >
                <option value={0}>No reminder</option>
                <option value={3}>3 months</option>
                <option value={6}>6 months</option>
                <option value={12}>12 months</option>
                <option value={24}>24 months</option>
              </select>
            </div>
            <div>
              <label className="label-tag">Or specific date</label>
              <input
                type="date"
                value={form.next_service_at.slice(0, 10)}
                onChange={(e) => setForm({ ...form, next_service_at: e.target.value })}
                className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm"
                data-testid={`asset-next-service-${card.id}`}
              />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button type="submit" disabled={busy || !form.label.trim()} className="label-tag px-4 py-2 bg-black text-white border-2 border-black brutal-btn text-xs disabled:opacity-50" data-testid={`asset-submit-${card.id}`}>{busy ? "Saving…" : (editingId ? "Save changes" : "Create asset")}</button>
            <button type="button" onClick={resetForm} className="label-tag px-4 py-2 bg-white border-2 border-black brutal-btn text-xs">Cancel</button>
          </div>
        </form>
      )}

      {loaded && assets.length === 0 && !showForm && (
        <div className="mt-3 text-xs text-neutral-500 italic">No assets yet — add one to mint a QR sticker.</div>
      )}

      {assets.length > 0 && (
        <>
          {/* Search bar — once you've got 20+ assets a vertical scroll
              dies. This filters by label, item, make, model, serial and
              asset code in one go. */}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search assets (label, make, code, serial…)"
              className="flex-1 min-w-[200px] px-3 py-2 border-2 border-black outline-none text-sm"
              data-testid={`asset-search-${card.id}`}
            />
            <div className="label-tag text-neutral-500 text-[10px]">
              {filteredSortedAssets.length} of {assets.length}
            </div>
          </div>
          <div className="mt-2 overflow-x-auto border-2 border-black bg-white">
            <table className="w-full text-xs">
              <thead className="bg-black text-white">
                <tr>
                  {[
                    { key: "label",          label: "Label" },
                    { key: "asset_code",     label: "Code" },
                    { key: "owner_card_code",label: "Owner" },
                    { key: "make",           label: "Make / model" },
                    { key: "next_service_at",label: "Service due" },
                    { key: "fault_reports",  label: "Faults" },
                    { key: "created_at",     label: "Added" },
                  ].map((col) => (
                    <th
                      key={col.key}
                      onClick={() => { if (sortKey === col.key) setSortDir(sortDir === "asc" ? "desc" : "asc"); else { setSortKey(col.key); setSortDir("asc"); } }}
                      className="text-left label-tag p-2 cursor-pointer select-none whitespace-nowrap"
                      data-testid={`asset-th-${col.key}-${card.id}`}
                    >
                      {col.label}{sortKey === col.key ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
                    </th>
                  ))}
                  <th className="label-tag p-2">·</th>
                </tr>
              </thead>
              <tbody>
                {filteredSortedAssets.map((a) => {
                  const due = a.next_service_at ? new Date(a.next_service_at) : null;
                  const dueSoon = due && (due.getTime() - Date.now()) < 1000 * 60 * 60 * 24 * 14;
                  const overdue = due && due.getTime() < Date.now();
                  return (
                    <React.Fragment key={a.id}>
                    <tr className="border-t border-neutral-300 hover:bg-[color:var(--cc-bg-2)]" data-testid={`asset-row-${a.asset_code}`}>
                      <td className="p-2 align-top">
                        <div className="font-display font-black leading-tight">{a.label}</div>
                        {a.notes && <div className="text-[10px] text-neutral-500 mt-0.5 line-clamp-1">{a.notes}</div>}
                      </td>
                      <td className="p-2 align-top">
                        <Link to={`/a/${a.asset_code}`} target="_blank" rel="noopener noreferrer" className="font-mono-cc text-[10px] underline" data-testid={`asset-link-${a.asset_code}`}>#{a.asset_code}</Link>
                      </td>
                      <td className="p-2 align-top whitespace-nowrap" data-testid={`asset-owner-cell-${a.asset_code}`}>
                        {a.owner_card_code
                          ? <span className="font-mono-cc text-[10px] px-1.5 py-0.5 bg-[color:var(--cc-bg-2)] border border-black">#{a.owner_card_code}</span>
                          : <span className="text-neutral-400 text-[10px]">—</span>}
                      </td>
                      <td className="p-2 align-top whitespace-nowrap">{[a.make, a.model].filter(Boolean).join(" · ") || "—"}</td>
                      <td className="p-2 align-top whitespace-nowrap">
                        {due ? (
                          <span className={`label-tag px-1.5 py-0.5 border border-black ${overdue ? "bg-red-500 text-white" : dueSoon ? "bg-[color:var(--cc-yellow)]" : "bg-white"}`}>
                            {a.next_service_at.slice(0, 10)}
                          </span>
                        ) : (
                          <span className="text-neutral-400">—</span>
                        )}
                      </td>
                      <td className="p-2 align-top text-center">
                        {a.fault_reports > 0
                          ? <span className="label-tag px-1.5 py-0.5 bg-red-500 text-white border border-black">{a.fault_reports}</span>
                          : <span className="text-neutral-400">0</span>}
                      </td>
                      <td className="p-2 align-top whitespace-nowrap text-neutral-500">{(a.created_at || "").slice(0, 10)}</td>
                      <td className="p-2 align-top text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => setHistoryOpenCode(historyOpenCode === a.asset_code ? null : a.asset_code)}
                          className={`p-1 border border-black mr-1 ${historyOpenCode === a.asset_code ? "bg-black text-white" : "bg-white"}`}
                          aria-label="Service history"
                          title="Service history & cost log"
                          data-testid={`asset-history-${a.asset_code}`}
                        >
                          <ClockCounterClockwise size={10} weight="bold" />
                        </button>
                        <button type="button" onClick={() => openEdit(a)} className="p-1 border border-black bg-[color:var(--cc-yellow)] mr-1" aria-label="Edit asset" data-testid={`asset-edit-${a.asset_code}`}>
                          <PencilSimple size={10} weight="bold" />
                        </button>
                        <button type="button" onClick={() => remove(a.id)} className="p-1 border border-black bg-white" aria-label="Delete asset" data-testid={`asset-delete-${a.asset_code}`}>
                          <X size={10} weight="bold" />
                        </button>
                      </td>
                    </tr>
                    {historyOpenCode === a.asset_code && (
                      <tr className="border-t border-neutral-200 bg-[color:var(--cc-bg-2)]" data-testid={`asset-history-row-${a.asset_code}`}>
                        <td colSpan={8} className="p-3">
                          {/* Constrain to viewport width on mobile so the
                              panel doesn't get pushed off-screen by the
                              parent table's overflow-x-auto. */}
                          <div className="sticky left-3 max-w-[calc(100vw-3rem)] sm:max-w-none">
                            <AssetServiceHistory
                              cardholderId={card.id}
                              asset={a}
                              onClose={() => setHistoryOpenCode(null)}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// AssetServiceHistory — inline panel under an asset row in the assets
// table. Shows the role-aware service history for the asset, plus a
// quick "+ Log service" mini-form. Renders cost prominently (the
// cardholder asked for cost visibility on the dashboard). The role-aware
// GET endpoint at /api/cardholder/assets/<asset_code>/service-events
// returns full detail (notes/materials/cost) for the current artisan
// and the homeowner; past artisans see only their own entries.
function AssetServiceHistory({ cardholderId, asset, onClose }) {
  const [events, setEvents] = React.useState([]);
  const [role, setRole] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [adding, setAdding] = React.useState(false);
  const [form, setForm] = React.useState({
    type: "service",
    summary: "",
    date: new Date().toISOString().slice(0, 10),
    km: "",
    materials: "",
    cost: "",
    notes: "",
    internal_only: false,
  });
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get(`/cardholder/assets/${asset.asset_code}/service-events`);
      setEvents(Array.isArray(data.events) ? data.events : []);
      setRole(data.role || "");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [asset.asset_code]);

  React.useEffect(() => { load(); }, [load]);

  async function submit(e) {
    e.preventDefault();
    if (!form.type.trim()) return setErr("Pick a type");
    setSaving(true);
    setErr("");
    try {
      await api.post(`/cardholder/cards/${cardholderId}/assets/${asset.id}/service-events`, {
        type: form.type.trim().slice(0, 40),
        summary: form.summary.trim().slice(0, 120),
        date: form.date || undefined,
        km: form.km ? Math.max(0, parseInt(form.km, 10) || 0) : null,
        materials: form.materials.trim().slice(0, 400) || undefined,
        cost: form.cost !== "" ? Number(form.cost) : null,
        notes: form.notes.trim().slice(0, 1000) || undefined,
        internal_only: !!form.internal_only,
      });
      // Reset the form (but keep type for quick repeat logging)
      setForm({
        type: form.type,
        summary: "",
        date: new Date().toISOString().slice(0, 10),
        km: "",
        materials: "",
        cost: "",
        notes: "",
        internal_only: false,
      });
      setAdding(false);
      toast.success("Service entry logged");
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  const total = events.reduce((s, e) => s + (typeof e.cost === "number" ? e.cost : 0), 0);

  return (
    <div className="border-2 border-black bg-white p-3" data-testid={`asset-service-history-${asset.asset_code}`}>
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <div>
          <div className="label-tag text-[10px] text-neutral-500">SERVICE HISTORY</div>
          <div className="font-display font-black text-base">{asset.label}</div>
        </div>
        <div className="flex items-center gap-2">
          {!adding && (
            <button
              type="button"
              onClick={() => { setAdding(true); setErr(""); }}
              className="label-tag bg-[color:var(--cc-yellow)] border-2 border-black px-3 py-1.5 brutal-btn text-xs inline-flex items-center gap-1"
              data-testid={`asset-service-add-${asset.asset_code}`}
            >
              <Plus size={11} weight="bold" /> Log service
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="label-tag bg-white border border-black px-2 py-1 text-xs"
            data-testid={`asset-service-close-${asset.asset_code}`}
          >
            Close
          </button>
        </div>
      </div>

      {role && (
        <div className="label-tag text-[10px] text-neutral-500 mb-2">
          Viewing as <strong>{role.replace("_", " ")}</strong>
          {events.length > 0 && (
            <> · {events.length} {events.length === 1 ? "entry" : "entries"}
              {total > 0 && <> · Lifetime spend <strong>R {total.toFixed(2)}</strong></>}
            </>
          )}
        </div>
      )}

      {err && (
        <div className="bg-red-50 border-2 border-red-600 text-red-800 p-2 text-xs mb-2" data-testid={`asset-service-error-${asset.asset_code}`}>
          {err}
        </div>
      )}

      {adding && (
        <form onSubmit={submit} className="border-2 border-black bg-[color:var(--cc-bg-2)] p-3 space-y-2 mb-3" data-testid={`asset-service-form-${asset.asset_code}`}>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label-tag">Type</label>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm bg-white"
                data-testid={`asset-service-type-${asset.asset_code}`}
              >
                <option value="service">Service</option>
                <option value="repair">Repair</option>
                <option value="inspection">Inspection</option>
                <option value="tires">Tyres</option>
                <option value="battery">Battery</option>
                <option value="alignment">Alignment</option>
                <option value="puncture">Puncture</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="label-tag">Date</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm"
                data-testid={`asset-service-date-${asset.asset_code}`}
              />
            </div>
          </div>
          <div>
            <label className="label-tag">Summary <span className="text-neutral-500">(public — visible on QR scan)</span></label>
            <input
              value={form.summary}
              onChange={(e) => setForm({ ...form, summary: e.target.value.slice(0, 120) })}
              placeholder="Element replaced, geyser flushed"
              className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm"
              data-testid={`asset-service-summary-${asset.asset_code}`}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label-tag">Cost (R) <span className="text-neutral-500">(private)</span></label>
              <input
                type="number"
                step="0.01"
                inputMode="decimal"
                value={form.cost}
                onChange={(e) => setForm({ ...form, cost: e.target.value })}
                placeholder="0.00"
                className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm font-mono-cc"
                data-testid={`asset-service-cost-${asset.asset_code}`}
              />
            </div>
            <div>
              <label className="label-tag">km <span className="text-neutral-500">(optional)</span></label>
              <input
                type="number"
                inputMode="numeric"
                value={form.km}
                onChange={(e) => setForm({ ...form, km: e.target.value })}
                placeholder="124500"
                className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm font-mono-cc"
              />
            </div>
          </div>
          <div>
            <label className="label-tag">Materials / parts <span className="text-neutral-500">(private)</span></label>
            <input
              value={form.materials}
              onChange={(e) => setForm({ ...form, materials: e.target.value.slice(0, 400) })}
              placeholder="2x Akebono ACT914 brake pads"
              className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm"
            />
          </div>
          <div>
            <label className="label-tag">Notes <span className="text-neutral-500">(private — full detail)</span></label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value.slice(0, 1000) })}
              rows={2}
              placeholder="Customer waiting on cambelt quote."
              className="mt-1 w-full px-2 py-1.5 border border-black outline-none text-sm resize-none"
            />
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={form.internal_only}
              onChange={(e) => setForm({ ...form, internal_only: e.target.checked })}
              data-testid={`asset-service-internal-${asset.asset_code}`}
            />
            <span>Internal only — hide from homeowner & public scan</span>
          </label>
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={saving}
              className="label-tag bg-black text-white border-2 border-black px-4 py-2 brutal-btn text-xs disabled:opacity-50"
              data-testid={`asset-service-submit-${asset.asset_code}`}
            >
              {saving ? "Saving…" : "Save entry"}
            </button>
            <button
              type="button"
              onClick={() => { setAdding(false); setErr(""); }}
              className="label-tag bg-white border border-black px-3 py-2 text-xs"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-sm text-neutral-600 py-3">Loading history…</div>
      ) : events.length === 0 ? (
        <div className="text-sm text-neutral-600 py-3 italic" data-testid={`asset-service-empty-${asset.asset_code}`}>
          No service entries yet. Click <strong>Log service</strong> above to add the first one.
        </div>
      ) : (
        <ol className="space-y-2">
          {events.map((ev, i) => (
            <li
              key={ev.id || i}
              className="border-l-2 border-black pl-3 py-1 bg-white"
              data-testid={`asset-service-entry-${asset.asset_code}-${i}`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="label-tag text-[10px] bg-black text-white px-1.5 py-0.5">{ev.type || "service"}</span>
                  {ev.internal_only && (
                    <span className="label-tag text-[10px] bg-red-600 text-white px-1.5 py-0.5">INTERNAL</span>
                  )}
                </div>
                <span className="font-mono-cc text-[11px] text-neutral-600 whitespace-nowrap">
                  {(ev.date || "").slice(0, 10)}
                </span>
              </div>
              {ev.summary && <div className="mt-1 text-sm font-medium leading-snug">{ev.summary}</div>}
              {ev.notes && <div className="mt-1 text-xs text-neutral-700 leading-snug whitespace-pre-wrap">{ev.notes}</div>}
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-neutral-600">
                {typeof ev.cost === "number" && (
                  <span><strong className="text-black">R {ev.cost.toFixed(2)}</strong></span>
                )}
                {ev.km && <span>{ev.km.toLocaleString()} km</span>}
                {ev.materials && <span className="truncate max-w-[260px]" title={ev.materials}>Parts: {ev.materials}</span>}
                <span>by {ev.author_name || "—"}</span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// FaultsTable — cross-asset fault report view for one card. Critical at
// scale: a plumber with 300 stickers needs one place to see all incoming
// faults, sorted/filterable, instead of clicking into 300 individual
// asset rows. Hidden when no faults exist.
function FaultsTable({ card }) {
  const [faults, setFaults] = React.useState([]);
  const [search, setSearch] = React.useState("");
  const [sortKey, setSortKey] = React.useState("created_at");
  const [sortDir, setSortDir] = React.useState("desc");

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/cardholder/cards/${card.id}/faults`);
        setFaults(data.faults || []);
      } catch { /* silent */ }
    })();
  }, [card.id]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = !q ? faults.slice() : faults.filter((f) => (
      `${f.asset_label || ""} ${f.asset_code || ""} ${f.reporter_name || ""} ${f.reporter_phone || ""} ${f.description || ""}`.toLowerCase().includes(q)
    ));
    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => String(a[sortKey] ?? "").localeCompare(String(b[sortKey] ?? "")) * dir);
    return list;
  }, [faults, search, sortKey, sortDir]);

  if (faults.length === 0) return null;

  return (
    <div className="mt-3 border-t border-dashed border-neutral-400 pt-3" data-testid={`faults-table-${card.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Wrench size={16} weight="bold" />
          <div className="label-tag">Fault Reports <span className="text-neutral-500">({faults.length})</span></div>
        </div>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search faults…"
          className="flex-1 min-w-[180px] max-w-xs px-2 py-1 border-2 border-black outline-none text-xs"
          data-testid={`faults-search-${card.id}`}
        />
      </div>
      <div className="mt-2 overflow-x-auto border-2 border-black bg-white">
        <table className="w-full text-xs">
          <thead className="bg-black text-white">
            <tr>
              {[
                { key: "created_at",     label: "When" },
                { key: "asset_label",    label: "Asset" },
                { key: "reporter_name",  label: "Reporter" },
                { key: "description",    label: "What's wrong" },
              ].map((col) => (
                <th
                  key={col.key}
                  onClick={() => { if (sortKey === col.key) setSortDir(sortDir === "asc" ? "desc" : "asc"); else { setSortKey(col.key); setSortDir("asc"); } }}
                  className="text-left label-tag p-2 cursor-pointer select-none whitespace-nowrap"
                >
                  {col.label}{sortKey === col.key ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((f) => (
              <tr key={f.id} className="border-t border-neutral-300" data-testid={`fault-row-${f.id}`}>
                <td className="p-2 align-top whitespace-nowrap text-neutral-500">{(f.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="p-2 align-top">
                  <div className="font-display font-black leading-tight">{f.asset_label || "—"}</div>
                  <Link to={`/a/${f.asset_code}`} target="_blank" rel="noopener noreferrer" className="font-mono-cc text-[10px] underline">#{f.asset_code}</Link>
                </td>
                <td className="p-2 align-top">
                  <div>{f.reporter_name}</div>
                  <a href={`https://wa.me/${(f.reporter_phone || "").startsWith("27") ? f.reporter_phone : "27" + (f.reporter_phone || "").replace(/^0/, "")}`} target="_blank" rel="noopener noreferrer" className="font-mono-cc text-[10px] underline text-green-700">{f.reporter_phone}</a>
                </td>
                <td className="p-2 align-top max-w-md">
                  <div className="line-clamp-2">{f.description}</div>
                  {f.photo_url && (
                    <a href={f.photo_url.startsWith("http") ? f.photo_url : `${process.env.REACT_APP_BACKEND_URL}${f.photo_url}`} target="_blank" rel="noopener noreferrer" className="label-tag text-[10px] underline mt-1 inline-block">View photo</a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// AutoDueInbox — Curb Auto merchant's "customers due for service"
// dashboard. Pulls /auto-due (license/tires/battery threshold logic on
// the backend), shows one row per customer with a one-tap "Push special"
// modal that emails them via the existing Gmail SMTP. This is the
// retention loop the user designed: shop sold the sticker → shop logged
// the work → shop pings the customer when due. Cost per push: R0.
function AutoDueInbox({ card }) {
  const [due, setDue] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);
  const [pushing, setPushing] = React.useState(null); // asset row currently being pushed
  const [subject, setSubject] = React.useState("");
  const [bodyTxt, setBodyTxt] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/cardholder/cards/${card.id}/auto-due`);
      setDue(data.due || []);
    } catch { /* silent */ }
    finally { setLoaded(true); }
  }, [card.id]);
  React.useEffect(() => { load(); }, [load]);

  const openPush = (row) => {
    setPushing(row);
    // Pre-fill with a sensible template based on the dominant reason.
    const reasons = row.reasons || [];
    if (reasons.includes("battery_age")) {
      setSubject("Your battery is due for a check");
      setBodyTxt(`Hi ${row.customer_name || "there"}, our records show we fitted your battery 3+ years ago. Bring the car in this month for a free test — R150 off a new battery if you need one.`);
    } else if (reasons.some((r) => r.startsWith("tires_") || r === "tires_age")) {
      setSubject("Time for new tires?");
      setBodyTxt(`Hi ${row.customer_name || "there"}, the tires we fitted are getting on. Book a free tread check this week and we'll knock R200 off a set of four.`);
    } else if (reasons.some((r) => r.startsWith("license_"))) {
      setSubject("Your license disc is expiring");
      setBodyTxt(`Hi ${row.customer_name || "there"}, your vehicle license expires soon. Renew before it lapses to avoid the late penalty.`);
    } else {
      setSubject(""); setBodyTxt("");
    }
  };

  const send = async (e) => {
    e?.preventDefault?.();
    if (!pushing || !subject.trim() || !bodyTxt.trim()) return;
    setBusy(true);
    try {
      await api.post(`/cardholder/cards/${card.id}/assets/${pushing.id}/push-special`, { subject, body: bodyTxt });
      toast.success(`Special sent to ${pushing.customer_name || pushing.customer_email}`);
      setPushing(null); setSubject(""); setBodyTxt("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return null;
  if (!due.length) {
    return (
      <div className="mt-3 border border-dashed border-[#16A34A] bg-[#F0FDF4] p-3 text-xs text-neutral-700" data-testid={`auto-due-empty-${card.id}`}>
        <span className="label-tag text-[#16A34A]">DUE-SOON INBOX</span> · No customer is due for service yet. As you log tire fits / battery swaps / license dates, this list will fill up.
      </div>
    );
  }
  return (
    <div className="mt-3 border-2 border-[#16A34A] bg-white" data-testid={`auto-due-${card.id}`}>
      <div className="bg-[#16A34A] text-white px-3 py-2 label-tag tracking-wider">DUE-SOON INBOX · {due.length}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-neutral-100 border-b border-neutral-300">
            <tr>
              <th className="text-left label-tag p-2">Customer</th>
              <th className="text-left label-tag p-2">Vehicle</th>
              <th className="text-left label-tag p-2">Due for</th>
              <th className="text-left label-tag p-2"></th>
            </tr>
          </thead>
          <tbody>
            {due.map((r) => (
              <tr key={r.id} className="border-t border-neutral-200" data-testid={`auto-due-row-${r.asset_code}`}>
                <td className="p-2 align-top">
                  <div className="font-display font-black leading-tight">{r.customer_name || "—"}</div>
                  <div className="text-[10px] text-neutral-600">{r.customer_email}</div>
                </td>
                <td className="p-2 align-top">
                  <div className="leading-tight">{r.label}</div>
                  <Link to={`/a/${r.asset_code}`} target="_blank" rel="noopener noreferrer" className="font-mono-cc text-[10px] underline">#{r.asset_code}</Link>
                </td>
                <td className="p-2 align-top">
                  <div className="flex flex-wrap gap-1">
                    {(r.reasons || []).map((rs) => {
                      const tone = rs.startsWith("license_") ? "bg-red-100 text-red-700 border-red-300" : rs === "tires_age" ? "bg-amber-100 text-amber-700 border-amber-300" : rs === "battery_age" ? "bg-blue-100 text-blue-700 border-blue-300" : "bg-neutral-100 text-neutral-700 border-neutral-300";
                      const label = rs === "license_expired" ? "license expired" : rs.startsWith("license_in_") ? rs.replace("license_in_", "lic in ").replace("_days", "d") : rs.replace("_", " ");
                      return <span key={rs} className={`label-tag px-1.5 py-0.5 border ${tone} text-[10px]`}>{label}</span>;
                    })}
                  </div>
                </td>
                <td className="p-2 align-top">
                  <button
                    type="button"
                    onClick={() => openPush(r)}
                    disabled={!r.customer_email}
                    className="label-tag px-2 py-1 bg-black text-white border border-black brutal-btn text-[10px] disabled:opacity-30"
                    data-testid={`auto-push-btn-${r.asset_code}`}
                  >
                    Push special
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pushing && (
        <div className="fixed inset-0 bg-black/60 z-40 flex items-end sm:items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setPushing(null)}>
          <form onSubmit={send} className="w-full max-w-md bg-white border-2 border-black" data-testid="auto-push-modal">
            <div className="bg-[#16A34A] text-white px-4 py-2 label-tag tracking-wider">PUSH SPECIAL · {pushing.customer_name || pushing.customer_email}</div>
            <div className="p-4 space-y-3">
              <div className="text-xs text-neutral-700">Sends a one-off marketing email to <strong>{pushing.customer_email}</strong>. Free via the Curb Crew mailer. Rate-limited to 1 push per customer per 24h.</div>
              <div>
                <label className="label-tag">Subject</label>
                <input value={subject} onChange={(e) => setSubject(e.target.value.slice(0, 120))} className="mt-1 w-full px-3 py-2 border border-black outline-none text-sm" data-testid="auto-push-subject" />
              </div>
              <div>
                <label className="label-tag">Body</label>
                <textarea value={bodyTxt} onChange={(e) => setBodyTxt(e.target.value.slice(0, 2000))} rows={5} className="mt-1 w-full px-3 py-2 border border-black outline-none text-sm resize-none" data-testid="auto-push-body" />
              </div>
              <div className="flex gap-2 pt-1">
                <button type="submit" disabled={busy || !subject.trim() || !bodyTxt.trim()} className="label-tag px-4 py-2 bg-black text-white border-2 border-black brutal-btn text-xs disabled:opacity-50" data-testid="auto-push-send">{busy ? "Sending…" : "Send"}</button>
                <button type="button" onClick={() => setPushing(null)} className="label-tag px-4 py-2 bg-white border-2 border-black brutal-btn text-xs">Cancel</button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}




// OwnedAssets — assets where this card is the HOMEOWNER (someone else
// installed/services it but the homeowner owns the physical thing). Lets
// the homeowner see their geyser/aircon/etc. across all the artisans
// they've engaged, and swap an artisan from here too without scanning.
function OwnedAssets({ card }) {
  const [assets, setAssets] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);
  const [swapFor, setSwapFor] = React.useState(null); // asset id being swapped
  const [newCode, setNewCode] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/cardholder/cards/${card.id}/owned-assets`);
      setAssets(data.assets || []);
    } catch { /* silent */ }
    finally { setLoaded(true); }
  }, [card.id]);

  React.useEffect(() => { load(); }, [load]);

  const swap = async (e) => {
    e.preventDefault();
    if (!swapFor || !newCode.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/cardholder/cards/${card.id}/assets/${swapFor}/swap-artisan`, { new_artisan_card_code: newCode.trim() });
      toast.success(`Service provider swapped to ${data.new_artisan.full_name}`);
      setSwapFor(null); setNewCode("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  if (loaded && assets.length === 0) return null;

  return (
    <div className="mt-3 border-t border-dashed border-neutral-400 pt-3" data-testid={`owned-assets-${card.id}`}>
      <div className="flex items-center gap-2 mb-1">
        <FilePdf size={16} weight="bold" />
        <div className="label-tag">Things I Own <span className="text-neutral-500">({assets.length})</span></div>
      </div>
      <div className="text-[11px] text-neutral-600 mb-2">Assets where you're the owner. If a service provider quits, swap them out here.</div>
      <div className="overflow-x-auto border-2 border-black bg-white">
        <table className="w-full text-xs">
          <thead className="bg-black text-white">
            <tr>
              <th className="text-left label-tag p-2">Asset</th>
              <th className="text-left label-tag p-2">Service Provider</th>
              <th className="text-left label-tag p-2">Service due</th>
              <th className="label-tag p-2 text-right">·</th>
            </tr>
          </thead>
          <tbody>
            {assets.map((a) => (
              <React.Fragment key={a.id}>
                <tr className="border-t border-neutral-300" data-testid={`owned-row-${a.asset_code}`}>
                  <td className="p-2 align-top">
                    <div className="font-display font-black leading-tight">{a.label}</div>
                    <Link to={`/a/${a.asset_code}`} target="_blank" rel="noopener noreferrer" className="font-mono-cc text-[10px] underline">#{a.asset_code}</Link>
                  </td>
                  <td className="p-2 align-top">
                    <div className="leading-tight">{a.artisan_name || "—"}</div>
                    <div className="font-mono-cc text-[10px] text-neutral-500">#{a.card_code}</div>
                  </td>
                  <td className="p-2 align-top whitespace-nowrap text-neutral-700">{a.next_service_at ? a.next_service_at.slice(0, 10) : "—"}</td>
                  <td className="p-2 align-top text-right">
                    <button
                      type="button"
                      onClick={() => { setSwapFor(swapFor === a.id ? null : a.id); setNewCode(""); }}
                      className={`label-tag px-2 py-1 border border-black brutal-btn text-[10px] ${swapFor === a.id ? "bg-neutral-200" : "bg-[color:var(--cc-yellow)]"}`}
                      data-testid={`owned-swap-${a.asset_code}`}
                    >
                      {swapFor === a.id ? "Cancel" : "Swap"}
                    </button>
                  </td>
                </tr>
                {swapFor === a.id && (
                  <tr className="border-t border-neutral-300 bg-[color:var(--cc-bg-2)]">
                    <td colSpan={4} className="p-2">
                      <form onSubmit={swap} className="flex flex-wrap items-end gap-2">
                        <div className="flex-1 min-w-[180px]">
                          <label className="label-tag">New service provider's card code</label>
                          <input
                            value={newCode}
                            onChange={(e) => setNewCode(e.target.value.toUpperCase().slice(0, 12))}
                            placeholder="e.g. Y629YE2A"
                            className="mt-1 w-full px-2 py-1.5 border-2 border-black outline-none text-sm font-mono-cc tracking-wider"
                            data-testid={`owned-swap-input-${a.asset_code}`}
                          />
                        </div>
                        <button type="submit" disabled={busy || !newCode.trim()} className="label-tag px-3 py-2 bg-black text-white border-2 border-black brutal-btn text-xs disabled:opacity-50" data-testid={`owned-swap-submit-${a.asset_code}`}>{busy ? "Swapping…" : "Swap"}</button>
                      </form>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function CardholderDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();

  const [cards, setCards] = useState([]);
  const [bottles, setBottles] = useState([]);
  const [error, setError] = useState("");
  const [linkCode, setLinkCode] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);
  const [editing, setEditing] = useState(null); // card being edited
  const [form, setForm] = useState({ full_name: "", skills: "", phone: "", bio: "", location: "", picture_url: "" });
  const [links, setLinks] = useState([]); // [{label, url, enabled}]
  const [linksEnabled, setLinksEnabled] = useState(false);
  const [sharePhoneInVcard, setSharePhoneInVcard] = useState(false);
  const [payEnabled, setPayEnabled] = useState(false);
  const [payPhone, setPayPhone] = useState("");
  const [payBank, setPayBank] = useState("");
  const [payTipEnabled, setPayTipEnabled] = useState(true);
  const [cardType, setCardType] = useState("curb_pro");
  const [travelOptIn, setTravelOptIn] = useState(false);
  const [travelFrom, setTravelFrom] = useState([]);
  const [travelTo, setTravelTo] = useState([]);
  // Curb Medic public fields — only persisted/exposed when card_type is
  // curb_medic but we keep the values in state regardless so a user who
  // toggles between types doesn't lose what they typed.
  const [medAllergies, setMedAllergies] = useState("");
  const [medChronic, setMedChronic] = useState("");
  const [medBloodGroup, setMedBloodGroup] = useState("");
  const [medAidPlan, setMedAidPlan] = useState("");
  const [medEmergency, setMedEmergency] = useState("");
  // Curb Traveller doc-vault — pasted URL + active toggle. The URL is
  // encrypted on the backend so the public scan never sees it; visitors
  // unlock via a 6-digit PIN that gets emailed to THIS cardholder.
  const [vaultLink, setVaultLink] = useState("");
  const [vaultActive, setVaultActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [viewLeads, setViewLeads] = useState(null); // card whose leads are shown
  const [leads, setLeads] = useState([]);
  // Auto-save infrastructure (Feb 2026 friction pivot). `dirty` flips on
  // every form/links change; a 1.5s debounce inside an effect calls the
  // backend in the background. `autoStatus` powers the inline "Saving…/
  // Saved ✓" badge. We also keep a `typeSaving / typeSaved` pair for the
  // optimistic card-type switcher so its feedback is independent of the
  // main form auto-save.
  const [dirty, setDirty] = useState(false);
  const [autoStatus, setAutoStatus] = useState(""); // "" | "saving" | "saved" | "error"
  const [typeSaving, setTypeSaving] = useState(false);
  const [typeSaved, setTypeSaved] = useState(false);
  // When openEdit() populates the form from a freshly loaded card, every
  // dep of the dirty effect "changes" — that would flip dirty=true and
  // trigger a no-op (or worse: stale-closure) auto-save on modal open. We
  // gate the first run with a ref so dirty only flips when the USER
  // actually edits something after the modal is on-screen.
  const skipDirtyOnce = useRef(false);

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "cardholder") nav("/cardholder/login");
  }, [user, loading, nav]);

  const load = async () => {
    try {
      const { data } = await api.get("/cardholder/cards");
      setCards(data.cards || []);
    } catch (e) { setError(formatApiError(e)); }
  };

  const loadBottles = async () => {
    try {
      const { data } = await api.get("/wine");
      setBottles(data.bottles || []);
    } catch (e) { setError(formatApiError(e)); }
  };

  const linkCard = async () => {
    const code = linkCode.trim().toUpperCase();
    if (!code) return toast.error("Enter a card code");
    // First card needs full activation (photo + skills) — send them there.
    if (cards.length === 0) {
      nav(`/activate/${code}`);
      return;
    }
    setLinkBusy(true);
    try {
      await api.post("/cardholder/cards/link", { code });
      toast.success(`Card ${code} linked`);
      setLinkCode("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLinkBusy(false);
    }
  };

  useEffect(() => { if (user?.role === "cardholder") { load(); loadBottles(); } }, [user]);

  // After activation, the success screen sends the cardholder here with
  // `?edit=first` so we auto-open the edit modal on the first card. They land
  // straight in their links + photo + bio editor without hunting for "Edit".
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get("edit") === "first" && cards.length && !editing) {
      openEdit(cards[0]);
      // Strip the param so a refresh doesn't keep re-opening the modal.
      const p = new URLSearchParams(params);
      p.delete("edit");
      setParams(p, { replace: true });
      // Defer the scroll so the modal has rendered.
      setTimeout(() => {
        const el = document.querySelector('[data-testid="edit-links-section"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
    }
  }, [cards, params]); // eslint-disable-line

  const openEdit = (c) => {
    setForm({
      full_name: c.full_name || "",
      skills: (c.skills || []).join(", "),
      phone: c.phone || "",
      bio: c.bio || "",
      location: c.location || "",
      picture_url: c.picture_url || "",
    });
    // Build link rows: keep existing, then pad missing presets so the user can fill them in.
    const existing = c.links || [];
    const merged = LINK_PRESETS.map((p) => {
      const found = existing.find((l) => (l.label || "").toLowerCase() === p.key.toLowerCase());
      return found
        ? { label: p.key, url: found.url || "", enabled: !!found.enabled }
        : { label: p.key, url: "", enabled: false };
    });
    setLinks(merged);
    setLinksEnabled(c.links_enabled !== false && existing.length > 0);
    setSharePhoneInVcard(!!c.share_phone_in_vcard);
    setPayEnabled(!!c.pay_enabled);
    setPayPhone(c.pay_phone || c.phone || "");
    setPayBank(c.pay_bank || "");
    // Tip jar default ON for new cardholders (key marketing hook); only off
    // if user explicitly turned it off.
    setPayTipEnabled(c.pay_tip_enabled !== false);
    setCardType(c.card_type || "curb_pro");
    setTravelOptIn(!!c.travel_opt_in);
    setTravelFrom(Array.isArray(c.travel_from) ? c.travel_from : []);
    setTravelTo(Array.isArray(c.travel_to) ? c.travel_to : []);
    setMedAllergies(c.medical_allergies || "");
    setMedChronic(c.medical_chronic || "");
    setMedBloodGroup(c.medical_blood_group || "");
    setMedAidPlan(c.medical_aid_plan || "");
    setMedEmergency(c.medical_emergency_contact || "");
    setVaultLink(c.vault_link || "");
    setVaultActive(!!c.vault_active);
    setError("");
    skipDirtyOnce.current = true;
    setEditing(c);
    // Just opened — nothing to auto-save yet.
    setDirty(false);
    setAutoStatus("");
    setTypeSaving(false);
    setTypeSaved(false);
  };

  // Mark the form as dirty whenever anything the auto-saver cares about
  // changes. We intentionally exclude `cardType` (handled by the instant
  // switcher) and the photo state (the upload itself saves immediately
  // via picture_url change which IS in this dep list).
  useEffect(() => {
    if (!editing) return;
    if (skipDirtyOnce.current) {
      // First post-openEdit run — values came from the loaded card, not
      // from a user edit. Reset the flag and DON'T mark dirty.
      skipDirtyOnce.current = false;
      return;
    }
    setDirty(true);
  }, [
    form.full_name, form.skills, form.phone, form.bio, form.location, form.picture_url,
    links, linksEnabled, sharePhoneInVcard,
    payEnabled, payPhone, payBank, payTipEnabled,
    travelOptIn, travelFrom, travelTo,
    medAllergies, medChronic, medBloodGroup, medAidPlan, medEmergency,
    vaultLink, vaultActive,
    editing,
  ]);

  // 1.5s debounced auto-save. Skips when there's no editing card OR no
  // dirty state OR an explicit save is mid-flight. Mirrors submitEdit's
  // validation but treats validation failures as "wait for the user" —
  // we DON'T surface them as toast spam mid-typing; the manual Save
  // button still shows the proper error.
  useEffect(() => {
    if (!editing || !dirty) return;
    const t = setTimeout(async () => {
      // Skip auto-save until the required fields look complete enough
      // that the backend won't reject the patch. Quiet skip — we'll try
      // again on the next change.
      if (!form.full_name.trim()) return;
      const skills = form.skills.split(",").map((s) => s.trim()).filter(Boolean);
      if (skills.length === 0) return;
      if (!/^0\d{9}$/.test(form.phone)) return;
      if (!form.picture_url) return;
      setAutoStatus("saving");
      try {
        const payloadLinks = links
          .filter((l) => (l.url || "").trim())
          .map((l) => ({ label: l.label, url: l.url.trim(), enabled: !!l.enabled }));
        await api.patch(`/cardholder/cards/${editing.id}`, {
          full_name: form.full_name, skills, phone: form.phone,
          bio: form.bio, location: form.location, picture_url: form.picture_url,
          links: payloadLinks,
          links_enabled: !!linksEnabled,
          share_phone_in_vcard: !!sharePhoneInVcard,
          pay_enabled: !!payEnabled,
          pay_phone: payPhone || "",
          pay_bank: payBank || "",
          // Tip jar is Curb Crew only — force off for every other type so
          // the public profile never accidentally shows a tipping vibe on
          // what's meant to be a pro/medic/auto card.
          pay_tip_enabled: cardType === "curb_crew" ? !!payTipEnabled : false,
          travel_opt_in: !!travelOptIn,
          travel_from: travelFrom,
          travel_to: travelTo,
          medical_allergies: medAllergies,
          medical_chronic: medChronic,
          medical_blood_group: medBloodGroup,
          medical_aid_plan: medAidPlan,
          medical_emergency_contact: medEmergency,
          vault_link: vaultLink,
          vault_active: !!vaultActive,
        });
        setAutoStatus("saved");
        setDirty(false);
        // Refresh the card list so the dashboard tile reflects new values
        // without closing the modal.
        load();
        // Fade "Saved ✓" away after a beat so the badge doesn't stick forever.
        setTimeout(() => setAutoStatus((s) => (s === "saved" ? "" : s)), 1800);
      } catch (e) {
        setAutoStatus("error");
      }
    }, 1500);
    return () => clearTimeout(t);
  }, [editing, dirty, form, links, linksEnabled, sharePhoneInVcard, payEnabled, payPhone, payBank, payTipEnabled, cardType, travelOptIn, travelFrom, travelTo, medAllergies, medChronic, medBloodGroup, medAidPlan, medEmergency, vaultLink, vaultActive]); // eslint-disable-line

  // Seamless card-type switch — optimistic UI update + background PATCH.
  // We don't go through the auto-save effect because:
  //  (a) the user wants instant feedback on tile click, not 1.5s later
  //  (b) card_type can change BEFORE the rest of the form is valid (e.g.
  //      a freshly activated card with no bio yet); auto-save would skip.
  const switchCardType = async (nextType) => {
    if (!editing || nextType === cardType) return;
    const prev = cardType;
    setCardType(nextType); // optimistic
    setTypeSaving(true);
    setTypeSaved(false);
    try {
      await api.patch(`/cardholder/cards/${editing.id}`, { card_type: nextType });
      setTypeSaved(true);
      load();
      setTimeout(() => setTypeSaved(false), 1500);
    } catch (e) {
      setCardType(prev); // rollback
      toast.error(formatApiError(e));
    } finally {
      setTypeSaving(false);
    }
  };

  const updateLink = (idx, patch) => {
    setLinks((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const handlePhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      const fd = new FormData();
      fd.append("file", compressed);
      const { data } = await api.post("/cardholder/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, picture_url: data.url }));
    } catch (e) { setError(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.full_name.trim()) return setError("Name required");
    const skills = form.skills.split(",").map((s) => s.trim()).filter(Boolean);
    if (skills.length === 0) return setError("Add at least one skill");
    if (!/^0\d{9}$/.test(form.phone)) return setError("Phone must be 10 digits starting with 0");
    if (!form.picture_url) return setError("A photo is required");
    setSubmitting(true);
    try {
      // Only send links that have a URL filled in. Backend accepts up to 8.
      const payloadLinks = links
        .filter((l) => (l.url || "").trim())
        .map((l) => ({ label: l.label, url: l.url.trim(), enabled: !!l.enabled }));
      await api.patch(`/cardholder/cards/${editing.id}`, {
        full_name: form.full_name, skills, phone: form.phone,
        bio: form.bio, location: form.location, picture_url: form.picture_url,
        links: payloadLinks,
        links_enabled: !!linksEnabled,
        share_phone_in_vcard: !!sharePhoneInVcard,
        pay_enabled: !!payEnabled,
        pay_phone: payPhone || "",
        pay_bank: payBank || "",
        // Tip jar is Curb Crew only — see auto-save above.
        pay_tip_enabled: cardType === "curb_crew" ? !!payTipEnabled : false,
        // card_type intentionally omitted — handled by switchCardType()
        // so the tile click is instant rather than waiting for Save.
        travel_opt_in: !!travelOptIn,
        travel_from: travelFrom,
        travel_to: travelTo,
        medical_allergies: medAllergies,
        medical_chronic: medChronic,
        medical_blood_group: medBloodGroup,
        medical_aid_plan: medAidPlan,
        medical_emergency_contact: medEmergency,
        vault_link: vaultLink,
        vault_active: !!vaultActive,
      });
      toast.success(linksEnabled ? "Saved · links visible on your card" : "Saved · links hidden from your card");
      setEditing(null);
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const reportLost = async (c) => {
    if (!window.confirm(`Report card #${c.code} as lost? It will stop working immediately and cannot be undone by you.`)) return;
    try {
      await api.post(`/cardholder/cards/${c.id}/report-lost`);
      await load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const openLeads = async (c) => {
    setViewLeads(c);
    try {
      const { data } = await api.get(`/cardholder/cards/${c.id}/leads`);
      setLeads(data.leads || []);
    } catch (e) { setError(formatApiError(e)); }
  };

  if (loading || !user) return <div><Nav /><div className="px-4 py-20 text-center label-tag">Loading…</div></div>;

  return (
    <div>
      <Nav />
      <section className="border-b border-black bg-[color:var(--cc-bg-2)]">
        <div className="max-w-6xl mx-auto px-4 py-10">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="label-tag text-neutral-500">My Curb Crew</div>
              <h1 className="font-display font-black text-3xl tracking-tighter mt-1">{user.name || user.email}</h1>
              <div className="label-tag text-neutral-700 mt-1">{user.email}</div>
            </div>
            <button onClick={() => logout().then(() => nav("/"))} className="label-tag px-3 py-2 border border-black brutal-btn flex items-center gap-2" data-testid="ch-logout">
              <SignOut size={14} weight="bold" /> Sign out
            </button>
          </div>

          {error && <div className="mt-4 label-tag text-red-600">{error}</div>}

          <div className="mt-6 bg-white border border-black p-4" data-testid="link-card-section">
            <div className="label-tag text-neutral-500">Link a card</div>
            <p className="mt-1 text-sm text-neutral-700">
              {cards.length === 0
                ? "Enter the code from your card slip — we'll take you to activation."
                : "Got another card? Enter its code to bind it to this account (copies your profile)."}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <input
                className="px-3 py-2 border border-black outline-none font-mono-cc uppercase tracking-wider flex-1 min-w-[10rem]"
                placeholder="CARD CODE"
                value={linkCode}
                onChange={(e) => setLinkCode(e.target.value)}
                data-testid="link-card-code"
              />
              <button
                type="button"
                disabled={linkBusy}
                onClick={linkCard}
                className="label-tag px-4 py-2 bg-black text-white brutal-btn disabled:opacity-60"
                data-testid="link-card-submit"
              >
                {linkBusy ? "Linking…" : cards.length === 0 ? "Activate" : "Link card"}
              </button>
            </div>
          </div>

          <div className="mt-4 bg-white border border-black p-4" data-testid="liquor-section">
            <div className="label-tag text-neutral-500">Liquor</div>
            {bottles.length === 0 ? (
              <p className="mt-2 text-sm text-neutral-600">No bottles registered on this account.</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {bottles.map((b) => (
                  <li key={b.code}>
                    <Link
                      to={`/wine/${b.code}`}
                      className="flex items-center gap-3 border border-black p-2"
                      data-testid={`liquor-${b.code}`}
                    >
                      {b.picture_url && (
                        <img
                          src={b.picture_url.startsWith("http") ? b.picture_url : `${BACKEND}${b.picture_url}`}
                          alt=""
                          className="w-12 h-12 object-cover border border-black"
                        />
                      )}
                      <span className="min-w-0">
                        <span className="font-display font-bold block truncate">{b.product_name}</span>
                        <span className="text-xs text-neutral-600">
                          {[b.producer, b.vintage, b.category].filter(Boolean).join(" · ")}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-4 bg-white border border-black p-4" data-testid="documents-section">
            <div className="label-tag text-neutral-500">Documents</div>
            <p className="mt-1 text-sm text-neutral-700">
              Stamp a qualification with a Twin-Matrix and keep the holder name we read off it.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => nav("/documents")}
                className="label-tag px-4 py-2 border border-black brutal-btn"
              >
                My documents
              </button>
              <button
                type="button"
                onClick={() => nav("/documents/new")}
                className="label-tag px-4 py-2 bg-black text-white brutal-btn"
              >
                Upload
              </button>
            </div>
          </div>

          <AccountSecurity />

          {/* Six-cards-one-wallet explainer — the user repeatedly asked us
              to make this clearer. Same plastic, different digital faces,
              all wired to one phone + email. Lives above the card grid so
              it's the first thing a returning cardholder sees. */}
          {cards.length > 0 && (
            <div className="mt-6 border-2 border-black bg-[color:var(--cc-bg-2)] p-4" data-testid="six-cards-explainer">
              <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                <div className="label-tag bg-[color:var(--cc-yellow)] px-2 py-1 inline-block self-start whitespace-nowrap">SIX CARDS · ONE WALLET</div>
                <div className="flex-1 min-w-0 text-sm text-neutral-800 leading-relaxed">
                  Think of this like a <strong>Mastercard with multiple accounts behind it</strong> — same plastic in your wallet, but you can flip the digital profile between <em>Curb Crew</em>, <em>Pro</em>, <em>Retailer</em>, <em>Medic</em>, <em>Traveller</em>, or <em>Auto</em> — and the new look goes live the moment someone scans your QR. All cards stay linked to your one phone and email.
                  <div className="mt-2 text-xs text-neutral-600">Tap <strong>Edit</strong> on a card below, then tap a card-type tile in the popup. It saves instantly — no extra click. Press <strong>View public</strong> any time to see exactly what a scanner sees.</div>
                </div>
              </div>
            </div>
          )}

          <div className={`mt-6 grid gap-4 ${cards.length <= 1 ? "grid-cols-1" : "sm:grid-cols-2"}`}>
            {cards.map((c) => {
              const lost = c.lost_reported || c.is_active === false;
              const vis = visualFor(c.card_type);
              return (
                <div key={c.id} className={`bg-white border border-black ${lost ? "opacity-60" : ""}`} data-testid={`my-card-${c.code}`}>
                  {/* Card-type accent strip — pulls the same colour family as
                      /cards so a cardholder instantly recognises which face
                      this card is currently showing. */}
                  <div className={`${vis.stripBg} ${vis.stripText} px-3 py-1.5 flex items-center justify-between border-b border-black`} data-testid={`card-type-strip-${c.code}`}>
                    <span className="label-tag tracking-wider">{vis.label}</span>
                    <span className="text-[10px] opacity-80">SHOWING NOW</span>
                  </div>
                  <div className="p-4">
                  <div className="flex items-start gap-3">
                    {c.picture_url && <img src={`${BACKEND}${c.picture_url}`} alt="" className="w-16 h-16 object-cover border border-black" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-black text-xl truncate">{c.full_name}</div>
                      <div className="font-mono-cc text-xs text-neutral-500">#{c.code}</div>
                      <div className="label-tag text-neutral-700 mt-1 truncate">{(c.skills || []).join(" · ")}</div>
                      {!lost && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          <div
                            className="inline-flex items-center gap-1.5 px-2 py-1 bg-black text-white label-tag border border-black"
                            data-testid={`scans-week-${c.code}`}
                            title="Public scans in the last 7 days"
                          >
                            <Eye size={12} weight="bold" />
                            {c.scans_this_week || 0} {((c.scans_this_week || 0) === 1) ? "scan" : "scans"} · 7d
                          </div>
                          <div
                            className="inline-flex items-center gap-1.5 px-2 py-1 bg-[color:var(--cc-yellow)] label-tag border border-black"
                            data-testid={`leads-month-${c.code}`}
                            title="Lead requests received in the last 30 days"
                          >
                            <ChatCircleText size={12} weight="bold" />
                            {c.leads_this_month || 0} {((c.leads_this_month || 0) === 1) ? "lead" : "leads"} · 30d
                          </div>
                        </div>
                      )}
                      {lost && <div className="label-tag text-red-600 mt-1">Reported lost</div>}
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <Link to={`/c/${c.code}`} className="label-tag px-3 py-2 border border-black brutal-btn" data-testid={`view-public-${c.code}`}>View public</Link>
                    {!lost && (
                      <>
                        <a
                          href={`https://wa.me/?text=${encodeURIComponent(`Check out my Curb Crew card 👉 ${window.location.origin}/c/${c.code}`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="label-tag px-3 py-2 bg-green-500 border border-black brutal-btn flex items-center gap-1"
                          data-testid={`share-wa-${c.code}`}
                        >
                          <WhatsappLogo size={14} weight="bold" /> Share
                        </a>
                        <button onClick={() => openEdit(c)} className="label-tag px-3 py-2 bg-[color:var(--cc-yellow)] border border-black brutal-btn flex items-center gap-1" data-testid={`edit-my-${c.code}`}>
                          <PencilSimple size={14} weight="bold" /> Edit
                        </button>
                        <button onClick={() => openLeads(c)} className="label-tag px-3 py-2 border border-black brutal-btn flex items-center gap-1" data-testid={`leads-my-${c.code}`}>
                          <ChatCircleText size={14} weight="bold" /> Leads
                        </button>
                        <Link to={`/print-flyers/${c.code}`} target="_blank" rel="noopener noreferrer" className="label-tag px-3 py-2 border border-black brutal-btn flex items-center gap-1" data-testid={`print-flyers-${c.code}`}>
                          <Printer size={14} weight="bold" /> Print flyers
                        </Link>
                        <button onClick={() => reportLost(c)} className="label-tag px-3 py-2 border border-black brutal-btn flex items-center gap-1 text-red-600" data-testid={`lost-my-${c.code}`}>
                          <Flag size={14} weight="bold" /> Lost
                        </button>
                        <Link to={`/checkpoint/${c.code}`} className="label-tag px-3 py-2 bg-black text-white border border-black brutal-btn flex items-center gap-1" data-testid={`checkpoint-my-${c.code}`}>
                          <ShieldCheck size={14} weight="bold" /> Checkpoint
                        </Link>
                      </>
                    )}
                  </div>
                  {!lost && <NoticeBoard card={c} />}
                  {!lost && (c.card_type === "curb_pro" || c.card_type === "curb_assets" || c.card_type === "curb_auto" || !c.card_type) && (
                    <>
                      <AssetManager card={c} />
                      <FaultsTable card={c} />
                      {c.card_type === "curb_auto" && <AutoDueInbox card={c} />}
                    </>
                  )}
                  {/* Things I own — every card can be a homeowner card,
                      not just Pro/Business. A Hustle gets to track their
                      own geyser too. */}
                  {!lost && <OwnedAssets card={c} />}
                  </div>
                </div>
              );
            })}
            {cards.length === 0 && (
              <div className="sm:col-span-2 bg-white border border-black p-6 text-center">
                <div className="label-tag text-neutral-500">No cards yet</div>
                <p className="mt-2 text-sm">Scan the QR on your card, or enter the code above to activate.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-black max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-black p-4">
              <div className="flex-1 min-w-0">
                <div className="label-tag text-neutral-500 flex items-center gap-2 flex-wrap">
                  Editing · <span className={`label-tag px-2 py-0.5 border border-black ${visualFor(cardType).chipBg}`} data-testid="edit-current-type-chip">{visualFor(cardType).label}</span>
                  {autoStatus === "saving" && <span className="text-neutral-700" data-testid="auto-status-saving">· Saving…</span>}
                  {autoStatus === "saved" && <span className="text-green-700" data-testid="auto-status-saved">· Saved ✓</span>}
                  {autoStatus === "error" && <span className="text-red-600" data-testid="auto-status-error">· Save failed</span>}
                </div>
                <div className="font-display font-black text-xl">#{editing.code}</div>
                <a
                  href={`/c/${editing.code}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-1 label-tag underline text-neutral-700"
                  data-testid="preview-public-link"
                >
                  <Eye size={12} weight="bold" /> Preview public view (what scanners see) ↗
                </a>
              </div>
              <button onClick={() => setEditing(null)} className="p-2" data-testid="edit-modal-close"><X size={20} weight="bold" /></button>
            </div>
            <form onSubmit={submitEdit} className="p-4 space-y-3" data-testid="my-edit-form">
              <div>
                <label className="label-tag">Card type</label>
                <div className="mt-1 grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[
                    { id: "curb_crew",      title: "Curb Crew",      blurb: "Tips + skills" },
                    { id: "curb_pro",       title: "Curb Pro",       blurb: "Skills + payment" },
                    { id: "curb_assets",    title: "Curb Retailer", blurb: "Shopfront + stock" },
                    { id: "curb_medic",     title: "Curb Medic",     blurb: "Medical / single-purpose" },
                    { id: "curb_traveller", title: "Curb Traveller", blurb: "Travel + driver" },
                    { id: "curb_auto",      title: "Curb Auto",      blurb: "Vehicle / logbook" },
                  ].map((t) => {
                    const tv = visualFor(t.id);
                    const selected = cardType === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => switchCardType(t.id)}
                        className={`text-left border-2 border-black px-2 py-2 brutal-btn text-xs ${selected ? `${tv.stripBg} ${tv.stripText}` : "bg-white"}`}
                        data-testid={`edit-card-type-${t.id}`}
                      >
                        <div className="font-display font-black text-sm">{t.title}</div>
                        <div className={`text-[10px] leading-tight ${selected ? "opacity-90" : "text-neutral-700"}`}>{t.blurb}</div>
                      </button>
                    );
                  })}
                </div>
                <div className="label-tag text-neutral-500 mt-1 flex items-center gap-2">
                  <span>Tap a tile — it saves instantly, no extra click.</span>
                  {typeSaving && <span className="text-neutral-700" data-testid="card-type-saving">Saving…</span>}
                  {typeSaved && !typeSaving && <span className="text-green-700" data-testid="card-type-saved">Saved ✓</span>}
                </div>
                {/* Per-type "what scanners see" blurb. Helps cardholders
                    understand what flipping the tile actually changes on
                    the public profile. */}
                <div className="mt-2 text-xs text-neutral-700 bg-[color:var(--cc-bg-2)] border border-dashed border-neutral-400 px-3 py-2" data-testid="card-type-blurb">
                  {cardType === "curb_crew"     && <>As <strong>Curb Crew</strong>, scanners see your photo, name, skills, lead form, and the Quick-pay tip jar (R5–R40 chips + PayShap). Best for car guards, waiters, vendors.</>}
                  {cardType === "curb_pro"      && <>As <strong>Curb Pro</strong>, scanners see your photo, name, skills, social media, documents, PayShap "Send me money" — but <strong>no tip jar</strong>. Asset stickers + fault reports stay on so callbacks come back to you.</>}
                  {cardType === "curb_assets"   && <>As <strong>Curb Retailer</strong>, scanners see your shop, catalogue, social media, lead form, product Asset QR stickers and fault reporting. Tip jar off, PayShap optional.</>}
                  {cardType === "curb_medic"    && <>As <strong>Curb Medic</strong>, scanners see your medical fields below (allergies, chronic condition, blood group, medical-aid plan, emergency contact) and a lead form. PayShap and tip jar are hidden — this card is built for crisis scanning.</>}
                  {cardType === "curb_traveller"&& <>As <strong>Curb Traveller</strong>, scanners see your photo, lead form, and (when active) a <strong>locked Doc Vault</strong> — paste a Google Drive link below, toggle the vault on, and visitors unlock it with a PIN we email to YOU when they ask.</>}
                  {cardType === "curb_auto"     && <>As <strong>Curb Auto</strong>, you're the workshop. Mint a sticker for every customer's vehicle, log tires / battery / VIN in 30 seconds, and the dashboard surfaces who's due for a callback. <strong>Public scan</strong> only shows emergency info (insurance, tow, license expiry) — full service history stays private to your shop.</>}
                </div>
              </div>
              <div>
                <label className="label-tag">Full name</label>
                <input className="mt-1 w-full px-4 py-3 border border-black outline-none" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required />
              </div>
              <div>
                <label className="label-tag">Skills (comma)</label>
                <input className="mt-1 w-full px-4 py-3 border border-black outline-none" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} required />
              </div>
              <div>
                <label className="label-tag">Phone (private)</label>
                <input className="mt-1 w-full px-4 py-3 border border-black outline-none font-mono-cc" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^0-9]/g, "") })} maxLength={10} inputMode="numeric" required />
              </div>
              <div>
                <label className="label-tag">Location</label>
                <input className="mt-1 w-full px-4 py-3 border border-black outline-none" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
              </div>

              {/* Curb Medic public fields — only shown when this card is
                  currently Curb Medic. Values persist in DB even after
                  the user switches type (privacy: backend only EXPOSES
                  them when card_type=curb_medic, but storage is durable
                  so flipping back and forth doesn't wipe data). */}
              {cardType === "curb_medic" && (
                <div className="border-2 border-[#DC2626] bg-[#FEF2F2] p-3 space-y-3" data-testid="medic-fields">
                  <div className="label-tag bg-[#DC2626] text-white px-2 py-1 inline-block">CURB MEDIC · PUBLIC FIELDS</div>
                  <div className="text-xs text-neutral-700">
                    These appear on your public card so anyone can read them in a crisis. Leave anything blank if it doesn't apply — empty fields stay hidden.
                  </div>
                  <div>
                    <label className="label-tag">Allergies</label>
                    <input
                      className="mt-1 w-full px-3 py-2 border border-black outline-none text-sm"
                      value={medAllergies}
                      onChange={(e) => setMedAllergies(e.target.value)}
                      placeholder="Penicillin, peanuts, bee stings…"
                      maxLength={240}
                      data-testid="medic-allergies"
                    />
                  </div>
                  <div>
                    <label className="label-tag">Chronic condition(s)</label>
                    <input
                      className="mt-1 w-full px-3 py-2 border border-black outline-none text-sm"
                      value={medChronic}
                      onChange={(e) => setMedChronic(e.target.value)}
                      placeholder="Type 1 diabetes, epilepsy, asthma…"
                      maxLength={240}
                      data-testid="medic-chronic"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="label-tag">Blood group</label>
                      <select
                        className="mt-1 w-full px-3 py-2 border border-black outline-none bg-white text-sm"
                        value={medBloodGroup}
                        onChange={(e) => setMedBloodGroup(e.target.value)}
                        data-testid="medic-blood-group"
                      >
                        <option value="">— Not stated —</option>
                        <option value="O+">O+</option>
                        <option value="O-">O-</option>
                        <option value="A+">A+</option>
                        <option value="A-">A-</option>
                        <option value="B+">B+</option>
                        <option value="B-">B-</option>
                        <option value="AB+">AB+</option>
                        <option value="AB-">AB-</option>
                      </select>
                    </div>
                    <div>
                      <label className="label-tag">Medical aid plan</label>
                      <select
                        className="mt-1 w-full px-3 py-2 border border-black outline-none bg-white text-sm"
                        value={medAidPlan}
                        onChange={(e) => setMedAidPlan(e.target.value)}
                        data-testid="medic-aid-plan"
                      >
                        <option value="">— Not stated —</option>
                        <option value="no_medical_aid">No medical aid</option>
                        <option value="hospital_plan">Hospital plan</option>
                        <option value="medical_plan">Medical plan</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="label-tag">Emergency contact number <span className="text-neutral-500">(optional, fully public)</span></label>
                    <input
                      type="tel"
                      inputMode="numeric"
                      className="mt-1 w-full px-3 py-2 border border-black outline-none font-mono-cc text-sm"
                      value={medEmergency}
                      onChange={(e) => setMedEmergency(e.target.value.replace(/[^\d+ ]/g, ""))}
                      placeholder="0821234567"
                      maxLength={32}
                      data-testid="medic-emergency"
                    />
                    <div className="label-tag text-neutral-500 mt-1">A friend or family member emergency services can call. Unlike your private phone above, THIS one IS shown on the public scan.</div>
                  </div>
                </div>
              )}

              {/* Curb Traveller doc-vault — paste a Google Drive / Dropbox
                  share link. We encrypt it on the server and only reveal
                  it after a visitor types a one-time PIN that gets mailed
                  to YOU (not to them). Per the user (Feb 2026): they ship
                  it knowing they don't want to host docs themselves. */}
              {cardType === "curb_traveller" && (
                <div className="border-2 border-[#0EA5E9] bg-[#F0F9FF] p-3 space-y-3" data-testid="vault-fields">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="label-tag bg-[#0EA5E9] text-white px-2 py-1 inline-block">CURB TRAVELLER · DOC VAULT</span>
                    <button
                      type="button"
                      onClick={() => setVaultActive((v) => !v)}
                      className={`flex items-center gap-2 px-3 py-2 border-2 border-black brutal-btn text-xs font-bold tracking-wide ${vaultActive ? "bg-green-500 text-black" : "bg-neutral-200 text-neutral-700"}`}
                      data-testid="vault-active-toggle"
                    >
                      <span className={`inline-block w-3 h-3 rounded-full ${vaultActive ? "bg-black" : "bg-neutral-500"}`} />
                      {vaultActive ? "VAULT ACTIVE" : "VAULT HIDDEN"}
                    </button>
                  </div>
                  <div className="text-xs text-neutral-700">
                    Paste a Google Drive / Dropbox / OneDrive <strong>share link</strong> to a folder with your passport, visa, insurance, hotel bookings, etc. We never see the docs — we just unlock the link for a visitor after they type a PIN. <strong>The PIN gets emailed to YOU</strong>, not to them — share it verbally when they ask.
                  </div>
                  <div>
                    <label className="label-tag">Vault share URL</label>
                    <input
                      type="url"
                      className="mt-1 w-full px-3 py-2 border border-black outline-none text-sm font-mono-cc"
                      value={vaultLink}
                      onChange={(e) => setVaultLink(e.target.value)}
                      placeholder="https://drive.google.com/drive/folders/…"
                      maxLength={600}
                      data-testid="vault-link"
                    />
                    <div className="label-tag text-neutral-500 mt-1">Make sure the link is <strong>set to "anyone with the link can view"</strong> in your Drive/Dropbox — otherwise the visitor will see a permissions error.</div>
                  </div>
                </div>
              )}
              <div>
                <label className="label-tag">Bio</label>
                <textarea rows={3} className="mt-1 w-full px-4 py-3 border border-black outline-none" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
              </div>

              {/* Media links — Premium */}
              <div className="border-t border-dashed border-neutral-400 pt-4" data-testid="edit-links-section">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="label-tag flex items-center gap-2">
                      <span className="bg-[color:var(--cc-yellow)] border border-black px-1.5 py-0.5 text-[10px]">PREMIUM</span>
                      Media links
                    </div>
                    <div className="text-xs text-neutral-600 mt-1">Show your socials & website on your public card.</div>
                  </div>
                  {/* Big obvious toggle — green when ON, gray when OFF */}
                  <button
                    type="button"
                    onClick={() => setLinksEnabled((v) => !v)}
                    className={`flex items-center gap-2 px-3 py-2 border-2 border-black brutal-btn text-xs font-bold tracking-wide ${linksEnabled ? "bg-green-500 text-black" : "bg-neutral-200 text-neutral-700"}`}
                    data-testid="links-master-toggle"
                  >
                    <span className={`inline-block w-3 h-3 rounded-full ${linksEnabled ? "bg-black" : "bg-neutral-500"}`} />
                    {linksEnabled ? "SHOWING ON CARD" : "HIDDEN FROM CARD"}
                  </button>
                </div>

                <div className={`mt-3 grid gap-2 ${linksEnabled ? "" : "opacity-50"}`}>
                  {links.map((l, i) => {
                    const Icon = linkIconFor(l.label);
                    const hasUrl = !!l.url.trim();
                    const isDocs = l.label.toLowerCase() === "documents";
                    return (
                      <div key={l.label}>
                        <div className={`flex items-center gap-2 border-2 px-2 py-1.5 ${hasUrl && l.enabled ? "border-black bg-[color:var(--cc-yellow)]/30" : "border-neutral-300"}`} data-testid={`link-row-${l.label.toLowerCase()}`}>
                          <Icon size={18} weight="bold" className="flex-shrink-0" />
                          <span className="label-tag w-20 flex-shrink-0">{l.label}</span>
                          <input
                            type="url"
                            inputMode="url"
                            placeholder={LINK_PRESETS[i]?.placeholder || "www.example.co.za"}
                            value={l.url}
                            onChange={(e) => updateLink(i, { url: e.target.value })}
                            className="flex-1 min-w-0 px-2 py-1.5 border border-black text-sm outline-none"
                            data-testid={`link-url-${l.label.toLowerCase()}`}
                          />
                          <button
                            type="button"
                            onClick={() => updateLink(i, { enabled: !l.enabled })}
                            disabled={!hasUrl || !linksEnabled}
                            className={`px-2 py-1 border border-black text-[10px] font-bold tracking-wide min-w-[44px] ${hasUrl && l.enabled && linksEnabled ? "bg-green-500 text-black" : "bg-neutral-200 text-neutral-600"} disabled:opacity-50 disabled:cursor-not-allowed`}
                            data-testid={`link-toggle-${l.label.toLowerCase()}`}
                          >
                            {hasUrl && l.enabled && linksEnabled ? "ON" : "OFF"}
                          </button>
                        </div>
                        {isDocs && (
                          <div className="text-[11px] text-neutral-600 px-2 mt-1 leading-relaxed">
                            📎 <strong>How to use Documents:</strong> upload your CV, references, ID copy or trade certificates to <span className="font-mono-cc">Google Drive</span> (free), <span className="font-mono-cc">Dropbox</span> or <span className="font-mono-cc">OneDrive</span>. Set the folder to <em>"Anyone with the link can view"</em>, then paste the share link here.<br/>
                            <span className="text-neutral-500">Curb Crew never stores your documents. You own them — you can add, remove, or replace files in your storage at any time.</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="text-[11px] text-neutral-500 mt-2">
                  Tip: just type <span className="font-mono-cc">www.instagram.com/yourname</span> — no need for <span className="font-mono-cc">http://</span>. We'll fix it up automatically. Each row needs to be turned ON. Use the big green button above to hide everything at once.
                </div>
              </div>

              {/* vCard phone sharing toggle */}
              <div className="border-t border-dashed border-neutral-400 pt-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="label-tag">Share my phone in "Save to contacts"</div>
                    <div className="text-xs text-neutral-600 mt-1">
                      When visitors tap "Save to my phone contacts" on your public card,
                      should your <strong>private phone number</strong> be included? Off keeps it private (they'll have to request it via the lead form).
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSharePhoneInVcard((v) => !v)}
                    className={`flex items-center gap-2 px-3 py-2 border-2 border-black brutal-btn text-xs font-bold tracking-wide ${sharePhoneInVcard ? "bg-green-500 text-black" : "bg-neutral-200 text-neutral-700"}`}
                    data-testid="vcard-phone-toggle"
                  >
                    <span className={`inline-block w-3 h-3 rounded-full ${sharePhoneInVcard ? "bg-black" : "bg-neutral-500"}`} />
                    {sharePhoneInVcard ? "PHONE INCLUDED" : "PHONE HIDDEN"}
                  </button>
                </div>
              </div>

              {/* PayShap / "Send me money" — South African banks support paying
                  to a cellphone number via PayShap. Cardholder opts in + provides
                  the cell linked to their bank. Public card shows the number with
                  3 simple steps so the visitor pays from their OWN banking app —
                  PayShap routes it to whichever bank the cardholder is registered
                  with. The bank field below is shown to visitors as a trust badge
                  only; visitors don't need to know it to pay. */}
              <div className="border-t border-dashed border-neutral-400 pt-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="label-tag">Let visitors send me money (PayShap)</div>
                    <div className="text-xs text-neutral-600 mt-1">
                      Adds a <strong>"Send me money"</strong> button on your public card. Visitors copy your PayShap cell number and pay from their own banking app — PayShap routes it to your bank automatically. <strong>Money goes straight to your bank — Curb Crew never touches it.</strong>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPayEnabled((v) => !v)}
                    className={`flex items-center gap-2 px-3 py-2 border-2 border-black brutal-btn text-xs font-bold tracking-wide ${payEnabled ? "bg-green-500 text-black" : "bg-neutral-200 text-neutral-700"}`}
                    data-testid="pay-enabled-toggle"
                  >
                    <span className={`inline-block w-3 h-3 rounded-full ${payEnabled ? "bg-black" : "bg-neutral-500"}`} />
                    {payEnabled ? "PAYSHAP ON" : "PAYSHAP OFF"}
                  </button>
                </div>
                {payEnabled && (
                  <div className="mt-3 space-y-3">
                    <div>
                      <label className="label-tag">Cell number linked to my bank (the one PayShap pays into)</label>
                      <input
                        type="tel"
                        inputMode="numeric"
                        value={payPhone}
                        onChange={(e) => setPayPhone(e.target.value.replace(/[^\d+]/g, ""))}
                        placeholder="0821234567"
                        className="mt-1 w-full px-3 py-2 border border-black outline-none font-mono-cc text-sm"
                        data-testid="pay-phone-input"
                      />
                      <div className="label-tag text-neutral-500 mt-1">
                        This MUST be the cell number registered with your bank for PayShap. If you've never used PayShap, ask your bank to link your cell to your account — usually free, takes 2 minutes via your banking app.
                      </div>
                    </div>
                    <div>
                      <label className="label-tag">My PayShap is with… <span className="text-neutral-500">(optional — shown on your card as a trust badge)</span></label>
                      <select
                        value={payBank}
                        onChange={(e) => setPayBank(e.target.value)}
                        className="mt-1 w-full px-3 py-2 border border-black outline-none bg-white text-sm"
                        data-testid="pay-bank-select"
                      >
                        <option value="">— Prefer not to say —</option>
                        <option value="Absa">Absa</option>
                        <option value="African Bank">African Bank</option>
                        <option value="Bank Zero">Bank Zero</option>
                        <option value="Capitec">Capitec</option>
                        <option value="Discovery Bank">Discovery Bank</option>
                        <option value="FNB">FNB</option>
                        <option value="Investec">Investec</option>
                        <option value="Nedbank">Nedbank</option>
                        <option value="Standard Bank">Standard Bank</option>
                        <option value="TymeBank">TymeBank</option>
                      </select>
                      <div className="label-tag text-neutral-500 mt-1">
                        Visitors don't need this to pay — PayShap routes by cell number. We just show it so they know which bank your money lands in.
                      </div>
                    </div>

                    {/* Tip Jar — fixed quick-tap chips (R5/R10/R20/R30/R40)
                        above the PayShap steps. Curb Crew ONLY (car guards,
                        waiters, vendors). Pro/Assets/Medic/Traveller/Auto
                        cards keep PayShap but hide the tip jar to avoid a
                        tipping vibe on what's meant to look professional. */}
                    {cardType === "curb_crew" && (
                      <div className="flex items-center justify-between gap-3 flex-wrap bg-[color:var(--cc-bg-2)] border border-black px-3 py-3">
                        <div className="flex-1 min-w-0">
                          <div className="label-tag">Quick-pay chips (R5 · R10 · R20 · R30 · R40)</div>
                          <div className="text-xs text-neutral-600 mt-1">
                            Adds quick-tap amounts above the PayShap steps so visitors can pay you in one tap — whether it's a R5 tip or a R40 service payment. Visitors can still type their own amount in their bank app. <strong>Great for car guards, petrol attendants, waiters, and small services.</strong>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setPayTipEnabled((v) => !v)}
                          className={`flex items-center gap-2 px-3 py-2 border-2 border-black brutal-btn text-xs font-bold tracking-wide ${payTipEnabled ? "bg-green-500 text-black" : "bg-neutral-200 text-neutral-700"}`}
                          data-testid="pay-tip-toggle"
                        >
                          <span className={`inline-block w-3 h-3 rounded-full ${payTipEnabled ? "bg-black" : "bg-neutral-500"}`} />
                          {payTipEnabled ? "QUICK-PAY ON" : "QUICK-PAY OFF"}
                        </button>
                      </div>
                    )}
                    {cardType !== "curb_crew" && (
                      <div className="text-xs text-neutral-500 italic" data-testid="tip-jar-hidden-note">
                        Quick-pay chips are only shown on Curb Crew cards (tips-first). PayShap "Send me money" stays on as long as it's toggled above.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Travel notifications opt-in — PARKED 2026-02
                  The taxi-trip / passenger-broadcast feature has been parked while
                  we work through legal positioning (SA NLTA Amendment Act 2023
                  e-hailing definition). We keep the `travel_opt_in / travel_from /
                  travel_to` DB fields populated for existing rows but hide the UI
                  so no new users opt in until the legal model is finalised.
                  Restore by uncommenting this block. */}

              <div>
                <label className="label-tag">Photo</label>
                <div className="mt-2 flex items-start gap-3">
                  <div className="w-24 h-24 border border-black bg-[color:var(--cc-bg-2)] grid place-items-center overflow-hidden flex-shrink-0">
                    {form.picture_url ? <img src={`${BACKEND}${form.picture_url}`} alt="" className="w-full h-full object-cover" /> : <Camera size={28} weight="bold" className="text-neutral-400" />}
                  </div>
                  <label className="label-tag px-4 py-3 border border-black brutal-btn cursor-pointer inline-flex items-center gap-2">
                    <Camera size={14} weight="bold" />
                    {uploading ? "Uploading…" : "Replace photo"}
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handlePhoto(e.target.files?.[0])} />
                  </label>
                </div>
              </div>
              {error && <div className="label-tag text-red-600">{error}</div>}
              <button type="submit" disabled={submitting} className="w-full bg-black text-white label-tag px-4 py-4 brutal-btn flex items-center justify-center gap-2 disabled:opacity-60" data-testid="my-edit-submit">
                <CheckCircle size={16} weight="bold" /> {submitting ? "Saving…" : "Save changes"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Leads modal */}
      {viewLeads && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-black max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-black p-4">
              <div>
                <div className="label-tag text-neutral-500">Leads for</div>
                <div className="font-display font-black text-xl">#{viewLeads.code}</div>
              </div>
              <button onClick={() => { setViewLeads(null); setLeads([]); }} className="p-2"><X size={20} weight="bold" /></button>
            </div>
            <div className="p-4">
              {leads.length === 0 && <div className="label-tag text-neutral-500">No leads yet.</div>}
              {leads.map((l) => (
                <div key={l.id} className="border-b border-neutral-200 py-3" data-testid={`my-lead-${l.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold">{l.interested_name}</div>
                    <a href={`tel:${l.interested_phone}`} className="font-mono-cc underline text-sm">{l.interested_phone}</a>
                  </div>
                  {l.message && <div className="text-sm text-neutral-700 mt-1">{l.message}</div>}
                  <div className="label-tag text-neutral-500 mt-1">{new Date(l.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
